import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/axiosConfig'; // ✅ Import axios config
import { 
  Search, Upload, FileText, Download, Home, RefreshCw, CheckCircle, AlertCircle, 
  File, User, Edit, Trash, Plus, X, Building, Copy, Star, ChevronDown, ChevronUp, 
  FileSpreadsheet, Brain, Sparkles, Zap, Users, BarChart, HelpCircle, Loader,
  Info, Shield, ArrowUpRight, Maximize, Server, LayoutGrid, Settings, AlignJustify,
  Layers, Trash2, UserPlus, UserX, Filter, List, Calendar, Image
} from 'lucide-react';
import localforage from 'localforage';

/**
 * Premium Room Management Component
 * Enhanced with bulk operations and improved room view
 */
const PremiumRoomManagement = ({ users = [], fetchUsers }) => {
  // Core data state
  const [roomAssignments, setRoomAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  
  // File handling state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [useSmartProcessing, setUseSmartProcessing] = useState(true);
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [showAiInfo, setShowAiInfo] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('roomview');
  const [showManualForm, setShowManualForm] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showCSVFormat, setShowCSVFormat] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [copySuccess, setCopySuccess] = useState('');
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [animatedCount, setAnimatedCount] = useState(false);
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [bulkOperationType, setBulkOperationType] = useState('none');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [roomFilter, setRoomFilter] = useState('');
  const [showRoomDetail, setShowRoomDetail] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  
  // Room view state
  const [roomsData, setRoomsData] = useState([]);
  const [roomViewMode, setRoomViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Room Images & Profile Images State
  const [roomImages, setRoomImages] = useState({}); // { roomNumber: imageUrl }
  const [roommateImages, setRoommateImages] = useState({}); // { userId: imageUrl }
  const [uploadingRoomImage, setUploadingRoomImage] = useState(null);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedRoomForImage, setSelectedRoomForImage] = useState(null);
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const [imageUploadPreview, setImageUploadPreview] = useState(null);
  
  // Form state
  const [manualFormData, setManualFormData] = useState({
    userId: null,
    email: '',
    roomNumber: '',
    building: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Animation refs
  const exportTextRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef([]);
  const suggestionRefs = useRef([]);
  const fileDropRef = useRef(null);
  
  // Constants
  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';
  const preferredUsers = ['neo.stutz@sunrise.net', 'roman.stammbacj@sunrise.net'];
  const aiModelVersion = "0.5";
  const aiUpdateDate = "15.03.2025";

  // Premium colors
  const COLORS = {
    light: {
      primary: '#da291c', // Indigo
      primaryDark: '#8d989b',
      primaryLight: '#a5b4fc',
      secondary: '#ef7c3c', // Pink
      secondaryDark: '#db2777',
      accent: '#99b2a7', // Cyan
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      surface: '#ffffff',
      surfaceAlt: '#f8fafc',
      surfaceHover: '#f1f5f9',
      border: '#e2e8f0',
      borderAlt: '#cbd5e1',
      text: '#0f172a',
      textMedium: '#334155',
      textLight: '#64748b'
    },
    dark: {
      primary: '#e2271e', // Indigo
      primaryDark: '#000',
      primaryLight: '#c7d2fe',
      secondary: '#f472b6', // Pink
      secondaryDark: '#ec4899',
      accent: '#22d3ee', // Cyan
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
      surface: '#000',
      surfaceAlt: '#1e293b',
      surfaceHover: '#334155',
      border: '#334155',
      borderAlt: '#475569',
      text: '#f8fafc',
      textMedium: '#cbd5e1',
      textLight: '#94a3b8'
    }
  };

  // Fetch room assignments
  const fetchRoomAssignments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');
  
      const response = await fetch(`${BASE_URL}/api/rooms/admin-assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
  
      if (!response.ok) throw new Error('Fehler beim Laden der Zimmerzuweisungen');
  
      const data = await response.json();
      
      // Sort by room number
      const sortedAssignments = data.roomAssignments?.sort((a, b) => {
        return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      }) || [];
      
      setRoomAssignments(sortedAssignments);
      
      // Process room data for room view
      processRoomsData(sortedAssignments);
      
      // Load room images for all rooms
      const uniqueRooms = [...new Set(sortedAssignments.map(a => a.roomNumber).filter(Boolean))];
      uniqueRooms.forEach(roomNumber => {
        fetchRoomImage(roomNumber);
      });
      
      // Calculate stats
      const allUsersCount = Array.isArray(users) ? users.length : 0;
      let assignedCount = sortedAssignments.length;
      
      setStats({
        total: allUsersCount,
        assigned: assignedCount,
        unassigned: allUsersCount - assignedCount
      });
      
      // Reset selected assignments when data changes
      setSelectedAssignments([]);
      
    } catch (err) {
      console.error('Error loading room assignments:', err);
      setError('Fehler beim Laden der Zimmerzuweisungen: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [BASE_URL]);

  // Process rooms data for the room view
  const processRoomsData = (assignments) => {
    // Group assignments by room number
    const roomsMap = {};
    
    assignments.forEach(assignment => {
      const roomKey = assignment.roomNumber;
      
      if (!roomsMap[roomKey]) {
        roomsMap[roomKey] = {
          roomNumber: assignment.roomNumber,
          building: assignment.building || '',
          users: []
        };
      }
      
      roomsMap[roomKey].users.push({
        userId: assignment.userId,
        username: assignment.username,
        email: assignment.email,
        isPreferred: preferredUsers.includes(assignment.email),
        profileImagePath: assignment.profileImagePath
      });
    });
    
    // Convert to array and sort by room number
    const roomsArray = Object.values(roomsMap).sort((a, b) => {
      return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
    });
    
    setRoomsData(roomsArray);
  };

  // Initial data load - runs only once to prevent auto-refresh loops
  useEffect(() => {
    fetchRoomAssignments();
  }, []); // Only run once on mount

  // Update stats when users prop changes (without refetching data)
  useEffect(() => {
    if (roomAssignments.length > 0) {
      const allUsersCount = Array.isArray(users) ? users.length : 0;
      let assignedCount = roomAssignments.length;
      
      setStats({
        total: allUsersCount,
        assigned: assignedCount,
        unassigned: allUsersCount - assignedCount
      });
    }
  }, [users, roomAssignments]);

  // Trigger animation only once when component mounts and has data
  useEffect(() => {
    if (roomAssignments.length > 0 && !animatedCount) {
      const timer = setTimeout(() => {
        setAnimatedCount(true);
      }, 500); // Small delay to ensure smooth animation
      return () => clearTimeout(timer);
    }
  }, [roomAssignments.length > 0, animatedCount]);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.scrollY > 20) {
          headerRef.current.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
          headerRef.current.style.backdropFilter = 'blur(12px)';
          headerRef.current.style.backgroundColor = isDarkMode 
            ? 'rgba(15, 23, 42, 0.85)' 
            : 'rgba(255, 255, 255, 0.85)';
        } else {
          headerRef.current.style.boxShadow = 'none';
          headerRef.current.style.backdropFilter = 'none';
          headerRef.current.style.backgroundColor = isDarkMode 
            ? COLORS.dark.surface 
            : COLORS.light.surface;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDarkMode]);
  
  // Drop zone hover effect
  useEffect(() => {
    if (!fileDropRef.current) return;
    
    const handleDragEnter = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.add('drag-over');
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.remove('drag-over');
    };
    
    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      fileDropRef.current.classList.remove('drag-over');
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        handleFileSelect({ target: { files: [file] } });
      }
    };
    
    const element = fileDropRef.current;
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);
    
    return () => {
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
    };
  }, []);

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0
  });

  // File handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
      
      // Display file type info
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let status = '';
      let confidence = 0;
      
      switch (fileExtension) {
        case 'xlsx':
        case 'xls':
          status = 'Excel-Datei erkannt. Die Smart-Verarbeitung kann Zeilen intelligent zuordnen.';
          confidence = 92;
          break;
        case 'csv':
          status = 'CSV-Datei erkannt. Die Smart-Verarbeitung kann Benutzernamen und E-Mails zuordnen.';
          confidence = 85;
          break;
        case 'txt':
          status = 'Text-Datei erkannt. Die Smart-Verarbeitung wird versuchen, Benutzer-Zimmer-Zuordnungen zu extrahieren.';
          confidence = 70;
          break;
        default:
          status = 'Unbekanntes Dateiformat. Die Verarbeitung könnte fehlschlagen.';
          confidence = 40;
      }
      
      setProcessingStatus(status);
      setConfidenceScore(confidence);
      
      // Simulate AI analyzing the file
      setIsAiAnalyzing(true);
      setTimeout(() => {
        setIsAiAnalyzing(false);
        setShowAiSuggestions(true);
      }, 2200);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte wähle zuerst eine Datei aus');
      return;
    }

    setUploadLoading(true);
    setError(null);
    setSuccessMessage('');
    setProcessingProgress(10);
    setShowAiSuggestions(false);
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use the smart endpoint if smart processing is enabled
      const endpoint = useSmartProcessing ? '/api/rooms/smart-import' : '/api/rooms/import';
      setProcessingProgress(30);
      
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      setProcessingProgress(70);
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Fehler beim Hochladen der Datei');
      }

      setProcessingProgress(90);
      setUploadResult(data);
      const successCount = data.successCount || 0;
      const totalProcessed = data.totalProcessed || 0;
      setSuccessMessage(`${successCount} von ${totalProcessed} Zimmerzuweisungen erfolgreich verarbeitet.`);
      setSelectedFile(null);
      
      // Reload room assignments after successful upload
      fetchRoomAssignments();
      // Update the main users list if fetchUsers is available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
      
      // Reset file input
      const fileInput = document.getElementById('room-file-upload');
      if (fileInput) fileInput.value = '';
      
      setProcessingProgress(100);
      
      // Auto reset progress after 2 seconds
      setTimeout(() => {
        setProcessingProgress(0);
      }, 2000);
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Fehler beim Hochladen der Datei');
      setProcessingProgress(0);
    } finally {
      setUploadLoading(false);
    }
  };

  // Search and filtering
  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  const handleRoomFilterChange = (e) => {
    setRoomFilter(e.target.value);
  };

  const clearRoomFilter = () => {
    setRoomFilter('');
  };

  const filteredAssignments = roomAssignments.filter(assignment => 
    assignment.username?.toLowerCase().includes(searchText.toLowerCase()) ||
    assignment.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    assignment.roomNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
    (assignment.building && assignment.building.toLowerCase().includes(searchText.toLowerCase()))
  );

  const filteredRooms = roomsData.filter(room =>
    room.roomNumber.toLowerCase().includes(roomFilter.toLowerCase()) ||
    (room.building && room.building.toLowerCase().includes(roomFilter.toLowerCase())) ||
    room.users.some(user => 
      user.username?.toLowerCase().includes(roomFilter.toLowerCase()) ||
      user.email?.toLowerCase().includes(roomFilter.toLowerCase())
    )
  );

  // User management functions
  const getUnassignedUsers = () => {
    if (!Array.isArray(users)) return [];
    const assignedUserIds = roomAssignments.map(assignment => assignment.userId);
    return users.filter(user => !assignedUserIds.includes(user.id));
  };

  const getSortedUnassignedUsers = () => {
    const unassignedUsers = getUnassignedUsers();
    
    return unassignedUsers.sort((a, b) => {
      // Preferred users first
      const aIsPreferred = preferredUsers.includes(a.email);
      const bIsPreferred = preferredUsers.includes(b.email);
      
      if (aIsPreferred && !bIsPreferred) return -1;
      if (!aIsPreferred && bIsPreferred) return 1;
      
      // Then sort alphabetically by username
      return a.username.localeCompare(b.username);
    });
  };

  // UI state management
  const toggleSection = (section) => {
    switch(section) {
      case 'csvFormat':
        setShowCSVFormat(!showCSVFormat);
        break;
      case 'aiSuggestions':
        setShowAiSuggestions(!showAiSuggestions);
        break;
      case 'aiInfo':
        setShowAiInfo(!showAiInfo);
        break;
      case 'bulkOperations':
        setShowBulkOperations(!showBulkOperations);
        break;
      default:
        break;
    }
  };

  const toggleExportPanel = () => {
    setShowExportPanel(!showExportPanel);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  const toggleViewMode = () => {
    setViewMode(viewMode === 'cards' ? 'table' : 'cards');
  };

  const toggleRoomViewMode = () => {
    setRoomViewMode(roomViewMode === 'grid' ? 'list' : 'grid');
  };

  // Form handling
  const validateForm = () => {
    const errors = {};
    
    if (formMode === 'add' && !manualFormData.email) {
      errors.email = 'Bitte wähle einen Benutzer aus';
    }
    
    if (!manualFormData.roomNumber) {
      errors.roomNumber = 'Bitte gib eine Zimmernummer ein';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setManualFormData({
      ...manualFormData,
      [name]: value
    });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const openAddForm = () => {
    setManualFormData({ userId: null, email: '', roomNumber: '', building: '' });
    setFormMode('add');
    setShowManualForm(true);
    setFormErrors({});
  };

  const openRoomDetail = (room) => {
    setSelectedRoom(room);
    setShowRoomDetail(true);
  };

  const closeRoomDetail = () => {
    setShowRoomDetail(false);
    setSelectedRoom(null);
  };

  const openAddUserToRoomForm = (roomNumber, building) => {
    setManualFormData({ 
      userId: null, 
      email: '', 
      roomNumber: roomNumber, 
      building: building || '' 
    });
    setFormMode('add');
    setShowManualForm(true);
    setFormErrors({});
  };

  const openEditForm = (assignment) => {
    setManualFormData({
      userId: assignment.userId,
      email: assignment.email,
      roomNumber: assignment.roomNumber,
      building: assignment.building || ''
    });
    setFormMode('edit');
    setShowManualForm(true);
    setFormErrors({});
  };

  const handleQuickAssignUser = (user) => {
    setManualFormData({
      userId: user.id,
      email: user.email,
      roomNumber: '',
      building: ''
    });
    setFormMode('add');
    setShowManualForm(true);
    setFormErrors({});
  };

  // ========== ROOM IMAGE FUNCTIONS ==========
  
  /**
   * Fetch room image for a specific room
   */
  const fetchRoomImage = async (roomNumber) => {
    try {
      const response = await axios.get(`/api/rooms/${roomNumber}/image`);
      
      if (response.data.hasImage) {
        // ✅ PRIORITY: Use serveUrl if available, fallback to imageUrl
        const imageUrlToUse = response.data.serveUrl || response.data.imageUrl;
        
        if (imageUrlToUse) {
          // Construct full URL - handle both relative and absolute paths
          let fullImageUrl = imageUrlToUse;
          
          // If it's a relative path, prepend BASE_URL
          if (!fullImageUrl.startsWith('http')) {
            // Ensure imageUrl starts with /
            fullImageUrl = fullImageUrl.startsWith('/') ? fullImageUrl : `/${fullImageUrl}`;
            fullImageUrl = `${BASE_URL}${fullImageUrl}`;
          }
          
          console.log('✅ Room image URL:', fullImageUrl);
          
          setRoomImages(prev => ({
            ...prev,
            [roomNumber]: fullImageUrl
          }));
          return fullImageUrl;
        }
      }
      return null;
    } catch (err) {
      console.error('Error fetching room image:', err);
      return null;
    }
  };

  /**
   * Fetch roommates with profile images
   */
  const fetchRoommatesWithImages = async (roomNumber) => {
    try {
      const response = await axios.get(`/api/rooms/${roomNumber}/roommates`);
      
      // Store profile images
      if (response.data.roommates) {
        const imageMap = {};
        response.data.roommates.forEach(roommate => {
          if (roommate.profileImageUrl) {
            imageMap[roommate.userId] = `${BASE_URL}${roommate.profileImageUrl}`;
          }
        });
        
        setRoommateImages(prev => ({ ...prev, ...imageMap }));
        return response.data.roommates;
      }
      return [];
    } catch (err) {
      console.error('Error fetching roommates with images:', err);
      return [];
    }
  };

  /**
   * Open room image upload modal
   */
  const openRoomImageUpload = (room) => {
    setSelectedRoomForImage(room);
    setShowImageUploadModal(true);
    setImageUploadFile(null);
    setImageUploadPreview(null);
  };

  /**
   * Handle image file selection
   */
  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      setImageUploadFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUploadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Upload room image
   */
  const handleUploadRoomImage = async () => {
    if (!imageUploadFile || !selectedRoomForImage) {
      setError('Please select an image');
      return;
    }
    
    setUploadingRoomImage(selectedRoomForImage.roomNumber);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', imageUploadFile);
      
      console.log('📤 Uploading room image...', {
        roomNumber: selectedRoomForImage.roomNumber,
        fileName: imageUploadFile.name,
        fileSize: imageUploadFile.size,
        fileType: imageUploadFile.type
      });
      
      // Use axios instance - interceptor will add auth token automatically
      // FormData handling is configured in axiosConfig.js
      const response = await axios.post(
        `/api/rooms/${selectedRoomForImage.roomNumber}/upload-image`,
        formData
      );
      
      console.log('✅ Upload response:', response.data);
      
      // Update room image in state
      // ✅ USE serveUrl (controller-served) instead of imageUrl (static)
      const imageUrlToUse = response.data.serveUrl || response.data.imageUrl;
      
      if (imageUrlToUse) {
        // Construct full URL - handle both relative and absolute paths
        let fullImageUrl = imageUrlToUse;
        
        // If it's a relative path, prepend BASE_URL
        if (!fullImageUrl.startsWith('http')) {
          // Ensure imageUrl starts with /
          fullImageUrl = fullImageUrl.startsWith('/') ? fullImageUrl : `/${fullImageUrl}`;
          fullImageUrl = `${BASE_URL}${fullImageUrl}`;
        }
        
        console.log('🖼️ Using serve URL:', response.data.serveUrl);
        console.log('🖼️ Full image URL:', fullImageUrl);
        
        setRoomImages(prev => ({
          ...prev,
          [selectedRoomForImage.roomNumber]: fullImageUrl
        }));
      }
      
      setSuccessMessage('Room image uploaded successfully!');
      setShowImageUploadModal(false);
      setImageUploadFile(null);
      setImageUploadPreview(null);
      
      // Refetch the image to ensure consistency
      await fetchRoomImage(selectedRoomForImage.roomNumber);
      
      setSelectedRoomForImage(null);
      
    } catch (err) {
      console.error('❌ Error uploading room image:', err);
      console.error('Error details:', {
        response: err.response?.data,
        status: err.response?.status,
        message: err.message,
        code: err.code
      });
      setError(err.response?.data?.message || err.message || 'Failed to upload room image');
    } finally {
      setUploadingRoomImage(null);
    }
  };

  /**
   * Fetch group leader for a room
   */
  const fetchRoomGroupLeader = async (roomNumber) => {
    try {
      const response = await axios.get(`/api/rooms/${roomNumber}/group-leader`);
      return response.data;
    } catch (err) {
      console.error('Error fetching room group leader:', err);
      return null;
    }
  };

  const handleSubmitManualForm = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError(null);
    setSuccessMessage('');
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      let email = manualFormData.email;
      
      // For edit mode without email, use existing assignment email
      if (formMode === 'edit' && !email) {
        const existingAssignment = roomAssignments.find(
          a => a.userId === manualFormData.userId
        );
        if (existingAssignment) {
          email = existingAssignment.email;
        }
      }

      // Create request body
      const requestBody = {
        email: email,
        roomNumber: manualFormData.roomNumber,
        building: manualFormData.building
      };

      // Make API call
      const response = await fetch(`${BASE_URL}/api/rooms/assign`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Zuweisen des Zimmers');
      }

      // Success
      setSuccessMessage(
        formMode === 'add'
          ? 'Zimmerzuweisung erfolgreich hinzugefügt'
          : 'Zimmerzuweisung erfolgreich aktualisiert'
      );
      
      // Close form and refresh data
      setShowManualForm(false);
      fetchRoomAssignments();
      // Update main users list if available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error assigning room:', err);
      setError(err.message || 'Fehler beim Zuweisen des Zimmers');
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!window.confirm(`Bist du sicher, dass du die Zimmerzuweisung für ${assignment.username} (Zimmer ${assignment.roomNumber}) löschen möchtest?`)) {
      return;
    }
    
    setError(null);
    setSuccessMessage('');
    
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');

      const response = await fetch(`${BASE_URL}/api/rooms/unassign`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: assignment.email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Löschen der Zimmerzuweisung');
      }

      // Success
      setSuccessMessage('Zimmerzuweisung erfolgreich gelöscht');
      
      // Refresh data
      fetchRoomAssignments();
      // Update main users list if available
      if (fetchUsers && typeof fetchUsers === 'function') {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting room assignment:', err);
      setError(err.message || 'Fehler beim Löschen der Zimmerzuweisung');
    }
  };

  // Export functions
  const generateExportData = () => {
    if (exportFormat === 'csv') {
      let csvContent = 'Name,Email,Zimmer,Gebäude\n';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building || '';
        csvContent += `${assignment.username},${assignment.email},${assignment.roomNumber},"${building}"\n`;
      });
      return csvContent;
    } else if (exportFormat === 'text') {
      let textContent = '';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building ? `(${assignment.building})` : '';
        textContent += `${assignment.username} - Zimmer ${assignment.roomNumber} ${building}\n`;
      });
      return textContent;
    } else if (exportFormat === 'list') {
      let listContent = '';
      filteredAssignments.forEach(assignment => {
        const building = assignment.building ? ` - ${assignment.building}` : '';
        listContent += `${assignment.username}\t${assignment.roomNumber}${building}\n`;
      });
      return listContent;
    }
    return '';
  };

  const downloadExportData = () => {
    const exportData = generateExportData();
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zimmerliste.${exportFormat}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    const exportData = generateExportData();
    navigator.clipboard.writeText(exportData)
      .then(() => {
        setCopySuccess('In die Zwischenablage kopiert!');
        setTimeout(() => setCopySuccess(''), 3000);
      })
      .catch(err => {
        console.error('Fehler beim Kopieren:', err);
        setCopySuccess('Fehler beim Kopieren!');
      });
  };

  // Bulk operations
  const handleToggleAssignmentSelection = (assignmentId) => {
    if (selectedAssignments.includes(assignmentId)) {
      setSelectedAssignments(selectedAssignments.filter(id => id !== assignmentId));
    } else {
      setSelectedAssignments([...selectedAssignments, assignmentId]);
    }
  };

  const handleSelectAllAssignments = () => {
    if (selectedAssignments.length === filteredAssignments.length) {
      // Deselect all
      setSelectedAssignments([]);
    } else {
      // Select all
      setSelectedAssignments(filteredAssignments.map(assignment => assignment.userId));
    }
  };

  const confirmBulkOperation = (message, callback) => {
    setConfirmationMessage(message);
    setConfirmationCallback(() => callback);
    setShowConfirmationDialog(true);
  };

  const handleConfirmation = () => {
    if (confirmationCallback) {
      confirmationCallback();
    }
    setShowConfirmationDialog(false);
  };

  const cancelConfirmation = () => {
    setShowConfirmationDialog(false);
    setConfirmationCallback(null);
  };

  const handleBulkDelete = async () => {
    if (selectedAssignments.length === 0) {
      setError('Bitte wähle mindestens eine Zuweisung aus');
      return;
    }

    confirmBulkOperation(
      `Bist du sicher, dass du ${selectedAssignments.length} Zimmerzuweisungen löschen möchtest?`,
      async () => {
        setError(null);
        setSuccessMessage('');
        
        try {
          const token = await localforage.getItem('token');
          if (!token) throw new Error('Kein Token gefunden');
          
          // Get emails for selected assignments
          const selectedEmails = filteredAssignments
            .filter(assignment => selectedAssignments.includes(assignment.userId))
            .map(assignment => assignment.email);
          
          let successCount = 0;
          let errorCount = 0;
          
          // Process each assignment sequentially
          for (const email of selectedEmails) {
            try {
              const response = await fetch(`${BASE_URL}/api/rooms/unassign`, {
                method: 'POST',
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
              });
              
              if (response.ok) {
                successCount++;
              } else {
                errorCount++;
              }
            } catch (err) {
              errorCount++;
            }
          }
          
          // Report results
          if (successCount > 0) {
            setSuccessMessage(`${successCount} Zimmerzuweisungen erfolgreich gelöscht${errorCount > 0 ? `, ${errorCount} fehlgeschlagen` : ''}`);
          } else {
            setError('Fehler beim Löschen der Zimmerzuweisungen');
          }
          
          // Refresh data
          fetchRoomAssignments();
          // Clear selection
          setSelectedAssignments([]);
          // Update main users list if available
          if (fetchUsers && typeof fetchUsers === 'function') {
            fetchUsers();
          }
        } catch (err) {
          console.error('Error in bulk delete:', err);
          setError(err.message || 'Fehler bei der Massenoperation');
        }
      }
    );
  };

  const handleDeleteAllRoomAssignments = () => {
    confirmBulkOperation(
      'Bist du sicher, dass du ALLE Zimmerzuweisungen löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden!',
      async () => {
        setError(null);
        setSuccessMessage('');
        
        try {
          const token = await localforage.getItem('token');
          if (!token) throw new Error('Kein Token gefunden');
          
          // Get all emails
          const allEmails = roomAssignments.map(assignment => assignment.email);
          
          let successCount = 0;
          let errorCount = 0;
          
          // Process each assignment sequentially
          for (const email of allEmails) {
            try {
              const response = await fetch(`${BASE_URL}/api/rooms/unassign`, {
                method: 'POST',
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
              });
              
              if (response.ok) {
                successCount++;
              } else {
                errorCount++;
              }
            } catch (err) {
              errorCount++;
            }
          }
          
          // Report results
          if (successCount > 0) {
            setSuccessMessage(`${successCount} Zimmerzuweisungen erfolgreich gelöscht${errorCount > 0 ? `, ${errorCount} fehlgeschlagen` : ''}`);
          } else {
            setError('Fehler beim Löschen der Zimmerzuweisungen');
          }
          
          // Refresh data
          fetchRoomAssignments();
          // Clear selection
          setSelectedAssignments([]);
          // Update main users list if available
          if (fetchUsers && typeof fetchUsers === 'function') {
            fetchUsers();
          }
        } catch (err) {
          console.error('Error in bulk delete:', err);
          setError(err.message || 'Fehler bei der Massenoperation');
        }
      }
    );
  };

  // Utility functions
  const getFileIcon = (filename) => {
    if (!filename) return <FileText size={18} />;
    
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet size={18} />;
      case 'csv':
        return <FileSpreadsheet size={18} />;
      case 'txt':
        return <FileText size={18} />;
      default:
        return <FileText size={18} />;
    }
  };

  const findUserByEmail = (email) => {
    if (!Array.isArray(users)) return null;
    return users.find(user => user.email === email);
  };

  // Calculate AI suggestions based on data
  const getAiSuggestions = () => {
    const unassignedCount = getUnassignedUsers().length;
    
    const suggestions = [];
    
    if (unassignedCount > 0) {
      suggestions.push({
        id: 'unassigned',
        message: `${unassignedCount} Lernende haben noch kein Zimmer zugewiesen.`,
        action: 'Zur Übersicht wechseln',
        handler: () => setActiveTab('unassigned'),
        priority: 'high'
      });
    }
    
    const usersWithoutBuilding = roomAssignments.filter(a => !a.building || a.building.trim() === '').length;
    if (usersWithoutBuilding > 0) {
      suggestions.push({
        id: 'missing-buildings',
        message: `${usersWithoutBuilding} Zimmerzuweisungen haben keine Gebäudeinformation.`,
        action: 'Tabelle anzeigen',
        handler: () => {
          setActiveTab('assigned');
          setViewMode('table');
        },
        priority: 'medium'
      });
    }
    
    if (selectedFile) {
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      if (extension === 'xlsx' || extension === 'xls') {
        suggestions.push({
          id: 'excel-detection',
          message: 'Excel-Datei erkannt. Smart-Verarbeitung kann komplexe Formate und mehrere Tabellenblätter verarbeiten.',
          action: 'Starten',
          handler: handleUpload,
          priority: 'high'
        });
      }
    }
    
    // Add recommendation for roommate optimization if there are enough assignments
    if (roomAssignments.length > 10) {
      suggestions.push({
        id: 'optimize-roommates',
        message: 'Optimierungspotential erkannt: Zimmer könnten effizienter nach Präferenzen und Profilen zugeteilt werden.',
        action: 'Mehr erfahren',
        handler: () => toggleSection('aiInfo'),
        priority: 'low'
      });
    }
    
    return suggestions;
  };

  // Color getters for current theme
  const getColor = (key) => {
    return isDarkMode ? COLORS.dark[key] : COLORS.light[key];
  };

  // Render subsections
  const renderNotificationBanner = () => {
    if (!successMessage && !error) return null;
    
    return (
      <div 
        className="notification-banner"
        style={{
          position: 'sticky',
          top: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 20px',
          borderRadius: '14px',
          marginBottom: '24px',
          fontSize: '15px',
          fontWeight: '500',
          zIndex: '10',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundColor: successMessage 
            ? isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5'
            : isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fef2f2',
          color: successMessage 
            ? isDarkMode ? COLORS.dark.success : COLORS.light.success
            : isDarkMode ? COLORS.dark.error : COLORS.light.error,
          borderLeft: `4px solid ${successMessage 
            ? isDarkMode ? COLORS.dark.success : COLORS.light.success
            : isDarkMode ? COLORS.dark.error : COLORS.light.error}`
        }}
      >
        {successMessage && (
          <>
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </>
        )}
        {error && (
          <>
            <AlertCircle size={20} />
            <span>{error}</span>
          </>
        )}
        <button 
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            }
          }}
          onClick={() => {
            if (successMessage) setSuccessMessage('');
            if (error) setError(null);
          }}
        >
          <X size={18} />
        </button>
      </div>
    );
  };

  const renderConfirmationDialog = () => {
    if (!showConfirmationDialog) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '110',
        animation: 'fadeIn 0.3s ease-out',
      }}>
        <div className="modal-content" style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
          borderRadius: '20px',
          boxShadow: `0 20px 60px rgba(0, 0, 0, ${isDarkMode ? '0.4' : '0.15'})`,
          padding: '28px',
          animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
            }}>
              <div style={{
                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                padding: '12px',
                borderRadius: '12px',
                color: getColor('error'),
                flexShrink: 0,
              }}>
                <AlertCircle size={24} />
              </div>
              <div style={{
                flex: 1,
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 8px 0',
                }}>Bestätigung erforderlich</h3>
                <p style={{
                  fontSize: '15px',
                  color: getColor('textMedium'),
                  margin: 0,
                  lineHeight: '1.5',
                }}>{confirmationMessage}</p>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
              <button 
                onClick={cancelConfirmation}
                style={{
                  padding: '10px 16px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: getColor('textMedium'),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Abbrechen
              </button>
              <button 
                onClick={handleConfirmation}
                style={{
                  padding: '10px 20px',
                  backgroundColor: getColor('error'),
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRoomView = () => (
    <div className="room-view-section" style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            color: getColor('accent'),
          }}>
            <Layers size={20} />
          </div>
          Zimmer-Übersicht
        </h3>
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{
            position: 'relative',
            maxWidth: '250px',
          }}>
            <input
              type="text"
              placeholder="Zimmer oder Benutzer suchen..."
              value={roomFilter}
              onChange={handleRoomFilterChange}
              style={{
                width: '100%',
                padding: '10px 36px 10px 16px',
                borderRadius: '10px',
                border: `1px solid ${getColor('border')}`,
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                color: getColor('text'),
              }}
            />
            {roomFilter && (
              <button
                onClick={clearRoomFilter}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: getColor('textLight'),
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={toggleRoomViewMode}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              backgroundColor: 'transparent',
              color: getColor('textLight'),
              border: `1px solid ${getColor('border')}`,
              borderRadius: '10px',
              cursor: 'pointer',
            }}
            title={roomViewMode === 'grid' ? 'Listenansicht' : 'Kachelansicht'}
          >
            {roomViewMode === 'grid' ? <List size={18} /> : <LayoutGrid size={18} />}
          </button>
          <button 
            onClick={openAddForm} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: getColor('success'),
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} />
            <span>Neue Zuweisung</span>
          </button>
        </div>
      </div>

      {/* Room Bulk Operations Panel */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
        marginBottom: '20px',
        borderRadius: '14px',
        border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <span style={{
            fontSize: '15px',
            fontWeight: '500',
            color: getColor('textMedium'),
          }}>
            Massenverwaltung:
          </span>
          <button
            onClick={handleDeleteAllRoomAssignments}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
              border: 'none',
              borderRadius: '8px',
              color: getColor('error'),
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={16} />
            <span>Alle löschen</span>
          </button>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{
            fontSize: '14px',
            color: getColor('textLight'),
          }}>
            {roomsData.length} Zimmer, {roomAssignments.length} Zuweisungen
          </span>
          <button
            onClick={() => fetchRoomAssignments()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              color: getColor('textMedium'),
              cursor: 'pointer',
            }}
            title="Aktualisieren"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 0',
          gap: '20px',
        }}>
          <div className="loading-pulse" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: getColor('primary'),
          }}>
            <Loader size={32} style={{
              animation: 'spin 1s linear infinite',
            }} />
          </div>
          <span style={{
            fontSize: '17px',
            color: getColor('textMedium'),
          }}>Laden...</span>
        </div>
      ) : filteredRooms.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 0',
          gap: '20px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '40px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
          }}>
            <Layers size={36} style={{
              color: getColor('accent'),
              opacity: 0.8,
            }} />
          </div>
          <p style={{
            fontSize: '17px',
            color: getColor('textMedium'),
            textAlign: 'center',
            maxWidth: '400px',
            lineHeight: '1.6',
          }}>
            {roomFilter ? 'Keine Zimmer gefunden, die den Suchkriterien entsprechen.' : 'Keine Zimmer gefunden. Fügen Sie neue Zimmerzuweisungen hinzu.'}
          </p>
        </div>
      ) : roomViewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px',
        }}>
          {filteredRooms.map(room => (
            <div
              key={room.roomNumber}
              className="room-card"
              style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
                border: `1px solid ${isDarkMode ? 'transparent' : getColor('border')}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
                },
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '48px',
                    height: '48px',
                    padding: '0 14px',
                    borderRadius: '12px',
                    backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                    color: getColor('accent'),
                    fontWeight: '700',
                    fontSize: '18px',
                  }}>
                    {room.roomNumber}
                  </div>
                  {room.building && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: getColor('textLight'),
                    }}>
                      <Building size={14} />
                      <span>{room.building}</span>
                    </div>
                  )}
                </div>              <div style={{
                display: 'flex',
                gap: '8px',
              }}>
                  <button
                    onClick={() => openRoomImageUpload(room)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '34px',
                      height: '34px',
                      backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : '#ede9fe',
                      border: 'none',
                      borderRadius: '8px',
                      color: getColor('primary'),
                      cursor: 'pointer',
                    }}
                    title="Raumbild hochladen"
                  >
                    <Image size={16} />
                  </button>
                  <button
                    onClick={() => openRoomDetail(room)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '34px',
                      height: '34px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                      border: 'none',
                      borderRadius: '8px',
                      color: getColor('textMedium'),
                      cursor: 'pointer',
                    }}
                    title="Details anzeigen"
                  >
                    <Maximize size={16} />
                  </button>
                  <button
                    onClick={() => openAddUserToRoomForm(room.roomNumber, room.building)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '34px',
                      height: '34px',
                      backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5',
                      border: 'none',
                      borderRadius: '8px',
                      color: getColor('success'),
                      cursor: 'pointer',
                    }}
                    title="Benutzer hinzufügen"
                  >
                    <UserPlus size={16} />
                  </button>
                </div>
              </div>
              
              {/* Room Image Section */}
              {roomImages[room.roomNumber] && (
                <div style={{
                  width: '100%',
                  height: '180px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                  position: 'relative',
                  marginBottom: '16px',
                }}>
                  <img 
                    src={roomImages[room.roomNumber]}
                    alt={`Zimmer ${room.roomNumber}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      console.error('❌ Failed to load image:', roomImages[room.roomNumber]);
                      e.target.style.display = 'none';
                      // Remove failed image from state
                      setRoomImages(prev => {
                        const newImages = { ...prev };
                        delete newImages[room.roomNumber];
                        return newImages;
                      });
                    }}
                    onLoad={(e) => {
                      console.log('✅ Image loaded successfully:', roomImages[room.roomNumber]);
                    }}
                  />
                </div>
              )}
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '4px',
              }}>
                <div style={{
                  fontSize: '14px',
                  color: getColor('textMedium'),
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <Users size={16} />
                  <span>Bewohner ({room.users.length})</span>
                </div>
                
                {room.users.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}>
                    {room.users.map(user => (
                      <div 
                        key={user.userId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                          borderRadius: '10px',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                          },
                        }}
                      >
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '18px',
                          overflow: 'hidden',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: getColor('textLight'),
                          flexShrink: 0,
                          position: 'relative',
                        }}>
                          {roommateImages[user.userId] ? (
                            <img 
                              src={roommateImages[user.userId]}
                              alt={user.username}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{
                            display: roommateImages[user.userId] ? 'none' : 'flex',
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {user.username?.split(' ').map(n => n[0]).join('')}
                          </div>
                        </div>
                        <div style={{
                          flex: 1,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: getColor('text'),
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {user.isPreferred && (
                              <Star size={14} style={{
                                color: getColor('warning'),
                                flexShrink: 0,
                              }} />
                            )}
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>{user.username}</span>
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: getColor('textLight'),
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {user.email}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const assignment = roomAssignments.find(a => a.userId === user.userId);
                            if (assignment) {
                              handleDeleteAssignment(assignment);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '30px',
                            height: '30px',
                            backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                            border: 'none',
                            borderRadius: '6px',
                            color: getColor('error'),
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                          title="Benutzer entfernen"
                        >
                          <UserX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '14px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: getColor('textLight'),
                    textAlign: 'center',
                  }}>
                    Dieses Zimmer ist leer
                  </div>
                )}
              </div>
              
              <div style={{
                marginTop: 'auto',
                paddingTop: '10px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <button
                  onClick={() => openAddUserToRoomForm(room.roomNumber, room.building)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                    color: getColor('primary'),
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <UserPlus size={16} />
                  <span>Benutzer hinzufügen</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          borderRadius: '16px',
          border: `1px solid ${getColor('border')}`,
          boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                }}>Zimmer</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                }}>Gebäude</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                }}>Bewohner</th>
                <th style={{
                  textAlign: 'right',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredRooms.map(room => (
                <tr
                  key={room.roomNumber}
                  style={{
                    borderBottom: `1px solid ${getColor('border')}`,
                  }}
                >
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '36px',
                      height: '36px',
                      padding: '0 12px',
                      borderRadius: '10px',
                      backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                      color: getColor('accent'),
                      fontWeight: '600',
                      fontSize: '15px',
                    }}>
                      {room.roomNumber}
                    </div>
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('textMedium'),
                    verticalAlign: 'middle',
                  }}>
                    {room.building ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: getColor('textMedium'),
                      }}>
                        <Building size={14} />
                        <span>{room.building}</span>
                      </div>
                    ) : (
                      <span style={{
                        color: getColor('textLight'),
                        fontStyle: 'italic',
                      }}>-</span>
                    )}
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    {room.users.length > 0 ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        {room.users.map(user => (
                          <div
                            key={user.userId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <div style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '14px',
                              overflow: 'hidden',
                              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: getColor('textLight'),
                            }}>
                              {user.username?.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}>
                              {user.isPreferred && (
                                <Star size={14} style={{
                                  color: getColor('warning'),
                                }} />
                              )}
                              <span>{user.username}</span>
                            </div>
                            <button
                              onClick={() => {
                                const assignment = roomAssignments.find(a => a.userId === user.userId);
                                if (assignment) {
                                  handleDeleteAssignment(assignment);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                                border: 'none',
                                borderRadius: '5px',
                                color: getColor('error'),
                                cursor: 'pointer',
                              }}
                              title="Benutzer entfernen"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{
                        color: getColor('textLight'),
                        fontStyle: 'italic',
                      }}>Keine Bewohner</span>
                    )}
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                    textAlign: 'right',
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end',
                    }}>
                      <button
                        onClick={() => openRoomDetail(room)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '34px',
                          height: '34px',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                          border: 'none',
                          borderRadius: '8px',
                          color: getColor('textMedium'),
                          cursor: 'pointer',
                        }}
                        title="Details anzeigen"
                      >
                        <Maximize size={16} />
                      </button>
                      <button
                        onClick={() => openAddUserToRoomForm(room.roomNumber, room.building)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 12px',
                          height: '34px',
                          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                          color: getColor('primary'),
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                        }}
                      >
                        <UserPlus size={16} style={{ marginRight: '6px' }} />
                        <span>Hinzufügen</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderRoomDetailModal = () => {
    if (!showRoomDetail || !selectedRoom) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '100',
        animation: 'fadeIn 0.3s ease-out',
      }}>
        <div 
          className="modal-content"
          style={{
            width: '100%',
            maxWidth: '640px',
            backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
            borderRadius: '20px',
            boxShadow: `0 20px 60px rgba(0, 0, 0, ${isDarkMode ? '0.4' : '0.15'})`,
            padding: '0',
            maxHeight: '90vh',
            overflow: 'hidden',
            animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div style={{
            padding: '24px',
            borderBottom: `1px solid ${getColor('border')}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                color: getColor('accent'),
              }}>
                <Home size={24} />
              </div>
              <div>
                <h3 style={{
                  margin: '0 0 4px 0',
                  fontSize: '20px',
                  fontWeight: '700',
                  color: getColor('text'),
                }}>
                  Zimmer {selectedRoom.roomNumber}
                </h3>
                {selectedRoom.building && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    color: getColor('textMedium'),
                  }}>
                    <Building size={14} />
                    <span>{selectedRoom.building}</span>
                  </div>
                )}
              </div>
            </div>
            <button 
              onClick={closeRoomDetail} 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                border: 'none',
                borderRadius: '18px',
                color: getColor('textMedium'),
                cursor: 'pointer',
              }}
            >
              <X size={20} />
            </button>
          </div>
          
          <div style={{
            overflow: 'auto',
            maxHeight: 'calc(90vh - 160px)',
            padding: '24px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <h4 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: getColor('text'),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Users size={18} />
                <span>Bewohner ({selectedRoom.users.length})</span>
              </h4>
              <button
                onClick={() => {
                  closeRoomDetail();
                  openAddUserToRoomForm(selectedRoom.roomNumber, selectedRoom.building);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5',
                  border: 'none',
                  borderRadius: '8px',
                  color: getColor('success'),
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <UserPlus size={16} />
                <span>Hinzufügen</span>
              </button>
            </div>
            
            {selectedRoom.users.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                {selectedRoom.users.map(user => (
                  <div 
                    key={user.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      borderRadius: '12px',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                      },
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: getColor('textLight'),
                      flexShrink: 0,
                    }}>
                      {user.username?.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div style={{
                      flex: 1,
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: getColor('text'),
                        marginBottom: '4px',
                      }}>
                        {user.isPreferred && (
                          <Star size={16} style={{
                            color: getColor('warning'),
                          }} />
                        )}
                        <span>{user.username}</span>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: getColor('textLight'),
                      }}>
                        {user.email}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const assignment = roomAssignments.find(a => a.userId === user.userId);
                        if (assignment) {
                          closeRoomDetail();
                          handleDeleteAssignment(assignment);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                        border: 'none',
                        borderRadius: '8px',
                        color: getColor('error'),
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                      title="Benutzer entfernen"
                    >
                      <UserX size={18} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: '40px 20px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                borderRadius: '12px',
                fontSize: '15px',
                color: getColor('textLight'),
                textAlign: 'center',
              }}>
                Dieses Zimmer ist noch nicht belegt
              </div>
            )}
          </div>
          
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${getColor('border')}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}>
            <button 
              onClick={closeRoomDetail}
              style={{
                padding: '10px 20px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                color: getColor('textMedium'),
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUploadSection = () => (
    <div className="upload-section" style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ede9fe',
            color: getColor('primary'),
          }}>
            <Sparkles size={20} />
          </div>
          Intelligente Dateiverarbeitung für Zimmerzuweisungen
          
          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              borderRadius: '50%',
              cursor: 'pointer',
              color: getColor('textLight'),
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('primary'),
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#ede9fe',
              }
            }}
            onClick={() => toggleSection('aiInfo')}
            title="KI Informationen"
          >
            <Info size={16} />
          </button>
        </h3>
      </div>
      
      {showAiInfo && (
        <div className="ai-info-panel" style={{
          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ede9fe',
                borderRadius: '12px',
                color: getColor('primary'),
              }}>
                <Brain size={20} />
              </div>
              <div>
                <h4 style={{
                  margin: '0 0 4px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: getColor('text'),
                }}>KI-Assistent für Zimmerverwaltung</h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                  }}>Version {aiModelVersion}</span>
                  <span style={{
                    fontSize: '13px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fce7f3',
                    color: isDarkMode ? COLORS.dark.secondary : COLORS.light.secondary,
                  }}>Beta</span>
                  <span style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                  }}>Aktualisiert: {aiUpdateDate}</span>
                </div>
              </div>
            </div>
            <button
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: getColor('textLight'),
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: getColor('text'),
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                }
              }}
              onClick={() => toggleSection('aiInfo')}
            >
              <X size={18} />
            </button>
          </div>
          
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(29, 35, 42, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: getColor('textMedium'),
          }}>
            <p style={{ marginTop: 0 }}>
              Unser KI-System ist darauf spezialisiert, komplexe Muster in Daten zu erkennen und 
              kann verschiedene Dateiformate (Excel, CSV, TXT) verarbeiten, um Benutzerdaten und Zimmerzuweisungen zu extrahieren.
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
              marginTop: '16px',
            }}>
              <div style={{
                flex: '1 1 280px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  color: getColor('text'),
                }}>
                  <Shield size={16} color={getColor('success')} /> Unterstützte Funktionen
                </span>
                <ul style={{
                  margin: '8px 0',
                  paddingLeft: '24px',
                }}>
                  <li>Intelligente Tabellenerkennung</li>
                  <li>Format- und Spaltenerkennung</li>
                  <li>Benutzer-Zimmer Zuordnung</li>
                  <li>Namens- und E-Mail-Matching</li>
                </ul>
              </div>
              <div style={{
                flex: '1 1 280px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  color: getColor('text'),
                }}>
                  <AlertCircle size={16} color={getColor('warning')} /> Einschränkungen
                </span>
                <ul style={{
                  margin: '8px 0',
                  paddingLeft: '24px',
                }}>
                  <li>Interpretation von Freitext kann ungenau sein</li>
                  <li>Bei großen Dateien kann die Verarbeitung länger dauern</li>
                  <li>Komplexe Formate brauchen eventuell manuelle Überprüfung</li>
                  <li>KI-Vorschläge sind noch in Entwicklung</li>
                </ul>
              </div>
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#ede9fe',
              border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: getColor('primary'),
              }}>
                <Info size={15} /> 
                Diese KI-Funktionen befinden sich noch in der Betaphase. Bei wichtigen Entscheidungen 
                empfehlen wir eine manuelle Überprüfung der Ergebnisse.
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
      }}>
        <div 
          ref={fileDropRef}
          className="file-drop-zone"
          style={{
            width: '100%',
            maxWidth: '740px',
          }}
        >
          <input
            id="room-file-upload"
            type="file"
            accept=".csv,.xls,.xlsx,.txt"
            onChange={handleFileSelect}
            style={{
              display: 'none',
            }}
          />
          <label 
            htmlFor="room-file-upload" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              padding: '48px 40px',
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
              border: `2px dashed ${isDarkMode ? getColor('borderAlt') : getColor('border')}`,
              borderRadius: '20px',
              cursor: 'pointer',
              color: getColor('textLight'),
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: getColor('primary'),
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.05)' : '#f0f7ff',
                boxShadow: `0 4px 20px rgba(99, 102, 241, 0.1)`,
              },
            }}
          >
            <div style={{
              display: 'flex',
              gap: '20px',
            }}>
              <FileSpreadsheet size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
              <FileText size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
              <FileSpreadsheet size={40} style={{
                color: getColor('primary'),
                opacity: 0.8,
              }} />
            </div>
            <span style={{
              fontSize: '18px',
              fontWeight: '500',
              color: getColor('textMedium'),
            }}>
              Excel, CSV oder Text-Datei hier ablegen oder klicken zum Auswählen
            </span>
            <span style={{
              fontSize: '15px',
              color: getColor('textLight'),
              maxWidth: '600px',
              textAlign: 'center',
              lineHeight: '1.6',
            }}>
              Die Datei sollte Benutzerdaten (Name oder E-Mail), Zimmernummern und optional Gebäude enthalten.
              Unser KI-System erkennt automatisch verschiedene Formate und Layouts.
            </span>
          </label>
        </div>
        
        {selectedFile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
            }}>
              {getFileIcon(selectedFile.name)}
              <span style={{ fontWeight: '500' }}>{selectedFile.name}</span>
              <span style={{
                fontSize: '13px',
                color: getColor('textLight'),
                marginLeft: '4px',
              }}>
                ({Math.round(selectedFile.size / 1024)} KB)
              </span>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)',
                border: 'none',
                borderRadius: '10px',
                color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.25)' : 'rgba(6, 182, 212, 0.2)',
                },
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {isAiAnalyzing && (
          <div className="ai-analyzing" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div className="pulse-loader" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#ede9fe',
              borderRadius: '14px',
              color: getColor('primary'),
              animation: 'pulse 1.5s ease-in-out infinite',
            }}>
              <Brain size={24} />
            </div>
            <div style={{
              flex: 1,
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: getColor('primary'),
                marginBottom: '4px',
              }}>
                KI analysiert Datei...
              </div>
              <div style={{
                fontSize: '14px',
                color: getColor('textMedium'),
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Struktur erkennen</span>
                  <span>√</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Datenformat identifizieren</span>
                  <span>√</span>
                </div>
                <div className="typing-dots" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>Muster und Beziehungen analysieren</span>
                  <span className="dots">•••</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {processingStatus && !isAiAnalyzing && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
            maxWidth: '740px',
            padding: '20px 24px',
            backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
            borderRadius: '16px',
            boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#ede9fe',
              borderRadius: '14px',
              color: getColor('primary'),
            }}>
              <Brain size={22} />
            </div>
            <div style={{
              flex: 1,
            }}>
              <div style={{
                fontSize: '15px',
                lineHeight: '1.5',
                color: getColor('textMedium'),
                marginBottom: '12px',
              }}>
                {processingStatus}
              </div>
              {confidenceScore > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div style={{
                    fontSize: '13px',
                    color: getColor('textLight'),
                    minWidth: '100px',
                  }}>
                    KI-Konfidenz
                  </div>
                  <div style={{
                    flex: 1,
                    height: '8px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${confidenceScore}%`,
                      backgroundColor: confidenceScore > 80 
                        ? getColor('success') 
                        : confidenceScore > 50 
                          ? getColor('warning') 
                          : getColor('error'),
                      borderRadius: '4px',
                      transition: 'width 0.8s cubic-bezier(0.65, 0, 0.35, 1)',
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: getColor('textMedium'),
                    minWidth: '45px',
                  }}>
                    {confidenceScore}%
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {showAiSuggestions && !isAiAnalyzing && (
          <div style={{
            width: '100%',
            maxWidth: '740px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#bae6fd'}`,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('aiSuggestions')}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '16px',
                fontWeight: '600',
                color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
              }}>
                <Sparkles size={18} style={{
                  color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                }} />
                <span>KI-Empfehlungen</span>
              </div>
              {showAiSuggestions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
            
            <div style={{
              padding: '16px',
            }}>
              {getAiSuggestions().map((suggestion, index) => (
                <div 
                  key={suggestion.id} 
                  ref={el => suggestionRefs.current[index] = el}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
                    borderRadius: '12px',
                    marginBottom: '10px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                    borderLeft: `3px solid ${
                      suggestion.priority === 'high' 
                        ? getColor('error')
                        : suggestion.priority === 'medium'
                          ? getColor('warning')
                          : getColor('success')
                    }`,
                    animation: 'fadeInUp 0.5s ease-out forwards',
                    animationDelay: `${index * 0.1}s`,
                    opacity: 0,
                    transform: 'translateY(10px)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <Zap size={18} style={{
                      color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                    }} />
                    <span style={{
                      fontSize: '14px',
                      color: getColor('textMedium'),
                    }}>{suggestion.message}</span>
                  </div>
                  <button 
                    onClick={suggestion.handler}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe',
                      color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.3)' : '#a5f3fc',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <span>{suggestion.action}</span>
                    <ArrowUpRight size={14} />
                  </button>
                </div>
              ))}

              {getAiSuggestions().length === 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '24px 16px',
                  color: getColor('textLight'),
                  fontSize: '15px',
                  textAlign: 'center',
                }}>
                  <HelpCircle size={20} />
                  <span>Keine spezifischen Empfehlungen für die aktuelle Datei verfügbar</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: '100%',
          maxWidth: '740px',
          padding: '20px 24px',
          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.05)' : '#fafaff',
          borderRadius: '16px',
          border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#e0e7ff'}`,
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={useSmartProcessing}
              onChange={(e) => setUseSmartProcessing(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: getColor('primary'),
              }}
            />
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '15px',
              fontWeight: '500',
              color: getColor('primary'),
            }}>
              <Brain size={16} />
              Smart-Verarbeitung aktivieren
            </span>
          </label>
          <div style={{
            fontSize: '14px',
            lineHeight: '1.5',
            color: getColor('textLight'),
            marginLeft: '30px',
          }}>
            Intelligente Verarbeitung erkennt verschiedene Formate, versteht komplexe Strukturen und 
            ordnet Daten selbst dann korrekt zu, wenn sie in ungewöhnlichen Layouts vorliegen.
          </div>
        </div>
        
        {processingProgress > 0 && (
          <div style={{
            width: '100%',
            maxWidth: '740px',
            height: '8px',
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div 
              style={{
                height: '100%',
                width: `${processingProgress}%`,
                backgroundColor: getColor('success'),
                borderRadius: '4px',
                transition: 'width 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="progress-shimmer" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.6)'}, 
                  transparent)`,
                animation: 'shimmer 1.5s infinite',
              }}></div>
            </div>
          </div>
        )}
        
        <button 
          onClick={handleUpload} 
          disabled={!selectedFile || uploadLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 28px',
            backgroundColor: getColor('primary'),
            color: '#ffffff',
            border: 'none',
            borderRadius: '14px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: !selectedFile || uploadLoading ? 'not-allowed' : 'pointer',
            boxShadow: `0 4px 14px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.2'})`,
            transition: 'all 0.3s ease',
            opacity: !selectedFile || uploadLoading ? 0.7 : 1,
            '&:hover': {
              backgroundColor: getColor('primaryDark'),
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 20px rgba(99, 102, 241, ${isDarkMode ? '0.4' : '0.3'})`,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {uploadLoading ? (
            <>
              <div className="spinner" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'spin 1s linear infinite',
              }}>
                <RefreshCw size={18} />
              </div>
              <span>Verarbeiten...</span>
            </>
          ) : (
            <>
              <Upload size={18} />
              <span>Datei hochladen und verarbeiten</span>
            </>
          )}
        </button>
      </div>

      {uploadResult && uploadResult.failCount > 0 && (
        <div style={{
          marginTop: '32px',
          backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fff1f2',
          borderRadius: '16px',
          padding: '20px 24px',
          maxWidth: '740px',
          margin: '0 auto',
          boxShadow: `0 4px 15px rgba(239, 68, 68, ${isDarkMode ? '0.15' : '0.07'})`,
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecdd3'}`,
            paddingBottom: '10px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <AlertCircle size={20} style={{
                color: getColor('error'),
              }} />
              <h4 style={{
                fontSize: '17px',
                fontWeight: '600',
                color: getColor('error'),
                margin: 0,
              }}>Nicht verarbeitete Einträge</h4>
            </div>
            <span style={{
              padding: '4px 10px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
              borderRadius: '12px',
              fontSize: '13px',
              color: getColor('error'),
              fontWeight: '500',
            }}>
              {uploadResult.failCount} von {uploadResult.totalProcessed}
            </span>
          </div>
          <div style={{
            maxHeight: '220px',
            overflowY: 'auto',
            padding: '4px',
          }}>
            {uploadResult.failedEntries.map((entry, idx) => (
              <div 
                key={idx} 
                style={{
                  padding: '12px 16px',
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
                  borderRadius: '10px',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: getColor('textMedium'),
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  animation: 'fadeInUp 0.3s ease-out forwards',
                  animationDelay: `${idx * 0.05}s`,
                  opacity: 0,
                  transform: 'translateY(10px)',
                }}
              >
                {entry}
              </div>
            ))}
          </div>
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : '#ffffff',
            borderRadius: '10px',
            fontSize: '13px',
            color: getColor('textLight'),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Info size={16} />
            <span>
              Tipp: Prüfe ob die E-Mail-Adressen korrekt sind und ob die Benutzer im System existieren.
            </span>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '32px',
      }}>
        <div style={{
          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
          borderRadius: '16px',
          padding: '20px',
          maxWidth: '800px',
          margin: '0 auto',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px 0',
          }}
          onClick={() => toggleSection('csvFormat')}>
            <h4 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '17px',
              fontWeight: '600',
              color: getColor('text'),
              margin: 0,
            }}>
              <FileSpreadsheet size={18} />
              Unterstützte Dateiformate
            </h4>
            {showCSVFormat ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          
          {showCSVFormat && (
            <div className="format-content" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginTop: '20px',
              animation: 'fadeIn 0.3s ease-out',
            }}>
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileSpreadsheet size={18} style={{ color: getColor('primary') }} />
                  Excel (.xlsx, .xls)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  Excel-Dateien können mehrere Spalten und Tabellenblätter enthalten. Die Smart-Verarbeitung erkennt automatisch, 
                  welche Spalten Benutzerdaten und Zimmernummern enthalten, sogar bei komplexen oder unregelmäßigen Layouts.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileSpreadsheet size={18} style={{ color: getColor('primary') }} />
                  CSV (.csv)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  CSV-Dateien werden intelligent analysiert. Idealerweise folgen sie diesem Format:
                </p>
                <div style={{
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f9fafb',
                  padding: '16px',
                  borderRadius: '10px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.7',
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  color: getColor('textMedium'),
                }}>
                  <code>username,room,building</code><br />
                  <code>neo.stutz@sunrise.net,101,Haupthaus</code><br />
                  <code>roman.stammbacj@sunrise.net,102,Nebengebäude</code>
                </div>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <h5 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: getColor('text'),
                  margin: '0 0 12px 0',
                }}>
                  <FileText size={18} style={{ color: getColor('primary') }} />
                  Text (.txt)
                </h5>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: getColor('textMedium'),
                  margin: '0 0 12px 0',
                }}>
                  Text-Dateien können unstrukturierte Daten enthalten. Die Smart-Verarbeitung 
                  versucht, Benutzer-Zimmer-Zuordnungen zu erkennen, selbst in frei formuliertem Text.
                </p>
                <div style={{
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f9fafb',
                  padding: '16px',
                  borderRadius: '10px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.7',
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  color: getColor('textMedium'),
                }}>
                  <code>neo.stutz@sunrise.net - Zimmer 101</code><br />
                  <code>Roman Stammbacj hat Zimmer 102 im Nebengebäude</code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderUnassignedUsersSection = () => (
    <div style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.1)' : '#fce7f3',
            color: getColor('secondary'),
          }}>
            <Users size={20} />
          </div>
          Lernende ohne Zimmer
        </h3>
        <div style={{
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={toggleViewMode}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              backgroundColor: 'transparent',
              color: getColor('textLight'),
              border: `1px solid ${getColor('border')}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('text'),
                borderColor: getColor('borderAlt'),
              },
            }}
            title={viewMode === 'cards' ? 'Tabelle anzeigen' : 'Karten anzeigen'}
          >
            {viewMode === 'cards' ? <AlignJustify size={18} /> : <LayoutGrid size={18} />}
          </button>
          <button 
            onClick={openAddForm} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: getColor('success'),
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: `0 4px 12px rgba(16, 185, 129, ${isDarkMode ? '0.3' : '0.2'})`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: isDarkMode ? '#059669' : '#059669',
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 15px rgba(16, 185, 129, ${isDarkMode ? '0.4' : '0.25'})`,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Plus size={16} />
            <span>Neue Zuweisung</span>
          </button>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
        }}>
          {getSortedUnassignedUsers().map(user => (
            <div 
              key={user.id} 
              className="user-card"
              style={{
                flex: '1 1 280px',
                maxWidth: '340px',
                backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
                border: `1px solid ${isDarkMode ? 'transparent' : getColor('border')}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                transition: 'all 0.3s ease',
                ...(preferredUsers.includes(user.email) 
                  ? {
                      borderLeft: `4px solid ${getColor('primary')}`,
                      backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.08)' : '#f5f3ff',
                    } 
                  : {}),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
                },
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 2px 6px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
                }}>
                  {user.profileImagePath ? (
                    <img 
                      src={user.profileImagePath.startsWith('http') ? user.profileImagePath : `${BASE_URL}${user.profileImagePath}`} 
                      alt={user.username} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }} 
                      onError={(e) => { e.target.src = '/api/placeholder/100/100'; }}
                    />
                  ) : (
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: getColor('textLight'),
                    }}>
                      {user.username.split(' ').map(name => name[0]).join('')}
                    </div>
                  )}
                </div>
                <div style={{
                  flex: 1,
                }}>
                  <h4 style={{
                    margin: '0 0 6px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: getColor('text'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    {preferredUsers.includes(user.email) && (
                      <Star size={16} style={{
                        color: getColor('warning'),
                      }} />
                    )}
                    {user.username}
                  </h4>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: getColor('textLight'),
                  }}>{user.email}</p>
                </div>
              </div>
              
              <div style={{
                marginTop: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <button
                  onClick={() => handleQuickAssignUser(user)}
                  className="user-assign-button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                    color: getColor('primary'),
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <Home size={16} />
                  <span>Zimmer zuweisen</span>
                </button>
              </div>
            </div>
          ))}
          
          {getUnassignedUsers().length === 0 && (
            <div style={{
              flex: '1 1 100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 0',
              gap: '20px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '40px',
                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#e0e7ff',
              }}>
                <Users size={36} style={{
                  color: getColor('primary'),
                  opacity: 0.8,
                }} />
              </div>
              <p style={{
                fontSize: '17px',
                color: getColor('textMedium'),
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: '1.6',
              }}>
                Alle Lernende haben bereits ein Zimmer zugewiesen.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          borderRadius: '16px',
          border: `1px solid ${getColor('border')}`,
          boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>Name</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>E-Mail</th>
                <th style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getColor('textMedium'),
                  borderBottom: `1px solid ${getColor('border')}`,
                  backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                  position: 'sticky',
                  top: 0,
                }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUnassignedUsers().map((user) => (
                <tr 
                  key={user.id} 
                  style={{
                    borderBottom: `1px solid ${getColor('border')}`,
                    backgroundColor: preferredUsers.includes(user.email) 
                      ? isDarkMode ? 'rgba(99, 102, 241, 0.08)' : '#f5f3ff'
                      : 'transparent',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDarkMode ? COLORS.dark.surfaceHover : '#f8fafc',
                    },
                  }}
                >
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {user.profileImagePath ? (
                          <img 
                            src={user.profileImagePath.startsWith('http') ? user.profileImagePath : `${BASE_URL}${user.profileImagePath}`} 
                            alt={user.username} 
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }} 
                            onError={(e) => { e.target.src = '/api/placeholder/72/72'; }}
                          />
                        ) : (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: getColor('textLight'),
                          }}>
                            {user.username.split(' ').map(name => name[0]).join('')}
                          </div>
                        )}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        {preferredUsers.includes(user.email) && (
                          <Star size={14} style={{
                            color: getColor('warning'),
                          }} />
                        )}
                        <span>{user.username}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('textMedium'),
                    verticalAlign: 'middle',
                  }}>{user.email}</td>
                  <td style={{
                    padding: '16px 20px',
                    fontSize: '14px',
                    color: getColor('text'),
                    verticalAlign: 'middle',
                  }}>
                    <button
                      onClick={() => handleQuickAssignUser(user)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff',
                        color: getColor('primary'),
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe',
                        },
                      }}
                    >
                      <Home size={14} />
                      <span>Zimmer zuweisen</span>
                    </button>
                  </td>
                </tr>
              ))}
              
              {getUnassignedUsers().length === 0 && (
                <tr>
                  <td colSpan={3} style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: getColor('textLight'),
                  }}>
                    Alle Lernende haben bereits ein Zimmer zugewiesen.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAssignmentsSection = () => (
    <div style={{
      marginBottom: '40px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: getColor('text'),
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
            color: getColor('accent'),
          }}>
            <BarChart size={20} />
          </div>
          Zimmerliste
        </h3>
        <div style={{
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={() => toggleSection('bulkOperations')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
              border: 'none',
              borderRadius: '10px',
              color: getColor('textMedium'),
              cursor: 'pointer',
            }}
            title="Massenoperationen"
          >
            <Layers size={20} />
          </button>
          <button
            onClick={toggleViewMode}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              backgroundColor: 'transparent',
              color: getColor('textLight'),
              border: `1px solid ${getColor('border')}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: getColor('text'),
                borderColor: getColor('borderAlt'),
              },
            }}
            title={viewMode === 'cards' ? 'Tabelle anzeigen' : 'Karten anzeigen'}
          >
            {viewMode === 'cards' ? <AlignJustify size={18} /> : <LayoutGrid size={18} />}
          </button>
          <button 
            onClick={toggleExportPanel}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: getColor('accent'),
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: `0 4px 12px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.2'})`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: isDarkMode ? '#0891b2' : '#0891b2',
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 15px rgba(6, 182, 212, ${isDarkMode ? '0.4' : '0.25'})`,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Download size={16} />
            <span>Exportieren</span>
          </button>
        </div>
      </div>

      {/* Bulk Operations Panel */}
      {showBulkOperations && (
        <div className="bulk-operations-panel" style={{
          backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#f5f3ff',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
          border: `1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#ddd6fe'}`,
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <h4 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: '600',
              color: getColor('primary'),
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <Layers size={16} />
              <span>Massenoperationen</span>
            </h4>
            <button
              onClick={() => toggleSection('bulkOperations')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                border: 'none',
                borderRadius: '6px',
                color: getColor('textMedium'),
                cursor: 'pointer',
              }}
            >
              <X size={16} />
            </button>
          </div>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <button
              onClick={handleSelectAllAssignments}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                border: 'none',
                borderRadius: '8px',
                color: getColor('textMedium'),
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              {selectedAssignments.length === filteredAssignments.length ? (
                <>
                  <X size={14} />
                  <span>Alle abwählen</span>
                </>
              ) : (
                <>
                  <CheckCircle size={14} />
                  <span>Alle auswählen</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleBulkDelete}
              disabled={selectedAssignments.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
                border: 'none',
                borderRadius: '8px',
                color: getColor('error'),
                fontSize: '14px',
                cursor: selectedAssignments.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedAssignments.length === 0 ? 0.6 : 1,
              }}
            >
              <Trash2 size={14} />
              <span>Ausgewählte löschen ({selectedAssignments.length})</span>
            </button>
            
            <button
              onClick={handleDeleteAllRoomAssignments}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fff5f5',
                border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'}`,
                borderRadius: '8px',
                color: getColor('error'),
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <Trash2 size={14} />
              <span>Alle Zuweisungen löschen</span>
            </button>
          </div>
          
          <div style={{
            fontSize: '13px',
            color: getColor('textLight'),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Info size={14} />
            <span>
              Hinweis: Massenoperationen können nicht rückgängig gemacht werden. Die Filter der 
              Suche werden bei Massenoperationen berücksichtigt.
            </span>
          </div>
        </div>
      )}

      {showExportPanel && (
        <div className="export-panel" style={{
          backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: `0 4px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: `1px solid ${getColor('border')}`,
            paddingBottom: '14px',
          }}>
            <h4 style={{
             margin: 0,
             fontSize: '18px',
             fontWeight: '600',
             color: getColor('text'),
           }}>Liste exportieren</h4>
           <button onClick={toggleExportPanel} style={{
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             padding: '8px',
             backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
             border: 'none',
             borderRadius: '10px',
             cursor: 'pointer',
             color: getColor('textMedium'),
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
               color: getColor('text'),
             },
           }}>
             <X size={18} />
           </button>
         </div>
         <div style={{
           display: 'flex',
           flexDirection: 'column',
           gap: '20px',
         }}>
           <div style={{
             display: 'flex',
             flexWrap: 'wrap',
             gap: '14px',
             alignItems: 'center',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               gap: '12px',
               minWidth: '120px',
             }}>
               <span style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Format:</span>
             </div>
             <div style={{
               display: 'flex',
               gap: '8px',
             }}>
               <button 
                 onClick={() => setExportFormat('csv')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'csv' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'csv'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'csv' ? '500' : 'normal',
                   color: exportFormat === 'csv'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'csv'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 CSV
               </button>
               <button 
                 onClick={() => setExportFormat('text')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'text' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'text'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'text' ? '500' : 'normal',
                   color: exportFormat === 'text'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'text'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 Text
               </button>
               <button 
                 onClick={() => setExportFormat('list')} 
                 style={{
                   padding: '10px 16px',
                   backgroundColor: exportFormat === 'list' 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#cffafe'
                     : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                   border: exportFormat === 'list'
                     ? `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.4)' : '#22d3ee'}`
                     : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                   borderRadius: '10px',
                   fontSize: '14px',
                   fontWeight: exportFormat === 'list' ? '500' : 'normal',
                   color: exportFormat === 'list'
                     ? isDarkMode ? COLORS.dark.accent : COLORS.light.accent
                     : getColor('textMedium'),
                   cursor: 'pointer',
                   transition: 'all 0.2s ease',
                   '&:hover': {
                     backgroundColor: exportFormat !== 'list'
                       ? isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'
                       : isDarkMode ? 'rgba(6, 182, 212, 0.25)' : '#a5f3fc',
                   },
                 }}
               >
                 Tabelliert
               </button>
             </div>
           </div>
           
           <div style={{
             display: 'flex',
             flexDirection: 'column',
             gap: '10px',
           }}>
             <span style={{
               fontSize: '15px',
               fontWeight: '500',
               color: getColor('textMedium'),
             }}>Vorschau:</span>
             <pre ref={exportTextRef} style={{
               backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#f8fafc',
               padding: '16px',
               borderRadius: '12px',
               fontFamily: 'monospace',
               fontSize: '13px',
               lineHeight: '1.6',
               whiteSpace: 'pre-wrap',
               maxHeight: '240px',
               overflowY: 'auto',
               color: getColor('textMedium'),
               border: `1px solid ${getColor('border')}`,
             }}>
               {generateExportData()}
             </pre>
           </div>
           
           <div style={{
             display: 'flex',
             justifyContent: 'flex-end',
             alignItems: 'center',
             gap: '16px',
             marginTop: '10px',
           }}>
             {copySuccess && (
               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 fontSize: '14px',
                 color: getColor('success'),
               }}>
                 <CheckCircle size={16} />
                 <span>{copySuccess}</span>
               </div>
             )}
             <button 
               onClick={copyToClipboard} 
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 padding: '12px 18px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 color: getColor('textMedium'),
                 border: `1px solid ${getColor('border')}`,
                 borderRadius: '12px',
                 fontSize: '14px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   color: getColor('text'),
                   transform: 'translateY(-2px)',
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               <Copy size={16} />
               <span>Kopieren</span>
             </button>
             <button 
               onClick={downloadExportData} 
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 padding: '12px 20px',
                 backgroundColor: getColor('accent'),
                 color: '#ffffff',
                 border: 'none',
                 borderRadius: '12px',
                 fontSize: '14px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 boxShadow: `0 4px 12px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.2'})`,
                 transition: 'all 0.3s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? '#0891b2' : '#0891b2',
                   transform: 'translateY(-2px)',
                   boxShadow: `0 6px 15px rgba(6, 182, 212, ${isDarkMode ? '0.4' : '0.25'})`,
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               <Download size={16} />
               <span>Herunterladen</span>
             </button>
           </div>
         </div>
       </div>
     )}

     {isLoading ? (
       <div style={{
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         justifyContent: 'center',
         padding: '60px 0',
         gap: '20px',
       }}>
         <div className="loading-pulse" style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           color: getColor('primary'),
           animation: 'pulse 1.5s ease-in-out infinite',
         }}>
           <Loader size={32} style={{
             animation: 'spin 1s linear infinite',
           }} />
         </div>
         <span style={{
           fontSize: '17px',
           color: getColor('textMedium'),
         }}>Laden...</span>
       </div>
     ) : filteredAssignments.length === 0 ? (
       <div style={{
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         justifyContent: 'center',
         padding: '60px 0',
         gap: '20px',
       }}>
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '80px',
           height: '80px',
           borderRadius: '40px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
         }}>
           <Home size={36} style={{
             color: getColor('accent'),
             opacity: 0.8,
           }} />
         </div>
         <p style={{
           fontSize: '17px',
           color: getColor('textMedium'),
           textAlign: 'center',
           maxWidth: '400px',
           lineHeight: '1.6',
         }}>
           {searchText ? 'Keine Ergebnisse gefunden.' : 'Keine Zimmerzuweisungen vorhanden.'}
         </p>
       </div>
     ) : viewMode === 'cards' ? (
       <div style={{
         display: 'flex',
         flexWrap: 'wrap',
         gap: '20px',
       }}>
         {filteredAssignments.map(assignment => (
           <div 
             key={assignment.userId} 
             className="room-card"
             style={{
               flex: '1 1 280px',
               maxWidth: '340px',
               backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
               borderRadius: '16px',
               padding: '24px',
               boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
               border: `1px solid ${isDarkMode ? 'transparent' : getColor('border')}`,
               display: 'flex',
               flexDirection: 'column',
               gap: '16px',
               transition: 'all 0.3s ease',
               ...(preferredUsers.includes(assignment.email) 
                 ? {
                     borderLeft: `4px solid ${getColor('accent')}`,
                     backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.08)' : '#ecfeff',
                   } 
                 : {}),
               '&:hover': {
                 transform: 'translateY(-4px)',
                 boxShadow: `0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'})`,
               },
             }}
           >
             <div style={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'flex-start',
             }}>
               <div style={{
                 display: 'flex',
                 flexDirection: 'column',
                 gap: '4px',
               }}>
                 <div style={{
                   display: 'inline-flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   minWidth: '48px',
                   height: '48px',
                   padding: '0 14px',
                   borderRadius: '12px',
                   backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                   color: getColor('accent'),
                   fontWeight: '700',
                   fontSize: '18px',
                 }}>
                   {assignment.roomNumber}
                 </div>
                 {assignment.building && (
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '6px',
                     marginTop: '4px',
                     fontSize: '13px',
                     color: getColor('textLight'),
                   }}>
                     <Building size={14} />
                     <span>{assignment.building}</span>
                   </div>
                 )}
               </div>
               <div style={{
                 display: 'flex',
                 gap: '6px',
               }}>
                 <div style={{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   width: '28px',
                   height: '28px',
                   borderRadius: '6px',
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                   color: getColor('textMedium'),
                   fontSize: '13px',
                   fontWeight: '600',
                   cursor: 'pointer',
                   '&:hover': {
                     backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0',
                   },
                 }}
                 onClick={() => handleToggleAssignmentSelection(assignment.userId)}
                 >
                   {selectedAssignments.includes(assignment.userId) ? (
                     <CheckCircle size={16} style={{ color: getColor('primary') }} />
                   ) : (
                     ''
                   )}
                 </div>
                 <button
                   onClick={() => openEditForm(assignment)}
                   style={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     width: '34px',
                     height: '34px',
                     backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                     border: 'none',
                     borderRadius: '8px',
                     color: getColor('textMedium'),
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                       color: getColor('text'),
                       transform: 'translateY(-2px)',
                     },
                   }}
                   title="Bearbeiten"
                 >
                   <Edit size={16} />
                 </button>
                 <button
                   onClick={() => handleDeleteAssignment(assignment)}
                   style={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     width: '34px',
                     height: '34px',
                     backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                     border: 'none',
                     borderRadius: '8px',
                     color: getColor('error'),
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca',
                       transform: 'translateY(-2px)',
                     },
                   }}
                   title="Löschen"
                 >
                   <Trash size={16} />
                 </button>
               </div>
             </div>
             
             <div style={{
               display: 'flex',
               alignItems: 'center',
               gap: '14px',
               marginTop: '12px',
             }}>
               <div style={{
                 width: '44px',
                 height: '44px',
                 borderRadius: '22px',
                 overflow: 'hidden',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 boxShadow: `0 2px 6px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
               }}>
                 {assignment.profileImagePath ? (
                   <img 
                     src={assignment.profileImagePath.startsWith('http') ? assignment.profileImagePath : `${BASE_URL}${assignment.profileImagePath}`} 
                     alt={assignment.username} 
                     style={{
                       width: '100%',
                       height: '100%',
                       objectFit: 'cover',
                     }} 
                     onError={(e) => { e.target.src = '/api/placeholder/88/88'; }}
                   />
                 ) : (
                   <div style={{
                     fontSize: '16px',
                     fontWeight: '600',
                     color: getColor('textLight'),
                   }}>
                     {assignment.username.split(' ').map(name => name[0]).join('')}
                   </div>
                 )}
               </div>
               <div style={{
                 flex: 1,
               }}>
                 <div style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px',
                   fontSize: '16px',
                   fontWeight: '600',
                   color: getColor('text'),
                 }}>
                   {preferredUsers.includes(assignment.email) && (
                     <Star size={15} style={{
                       color: getColor('warning'),
                     }} />
                   )}
                   <span>{assignment.username}</span>
                 </div>
                 <div style={{
                   fontSize: '13px',
                   color: getColor('textLight'),
                   marginTop: '2px',
                 }}>
                   {assignment.email}
                 </div>
               </div>
             </div>
             
             {assignment.assignmentDate && (
               <div style={{
                 marginTop: '8px',
                 fontSize: '13px',
                 color: getColor('textLight'),
                 display: 'flex',
                 alignItems: 'center',
                 gap: '6px',
               }}>
                 <span>Letztes Update:</span>
                 <span>{new Date(assignment.assignmentDate).toLocaleString('de-DE')}</span>
               </div>
             )}
           </div>
         ))}
       </div>
     ) : (
       <div style={{
         overflowX: 'auto',
         borderRadius: '16px',
         border: `1px solid ${getColor('border')}`,
         boxShadow: `0 4px 15px rgba(0, 0, 0, ${isDarkMode ? '0.15' : '0.05'})`,
       }}>
         <table style={{
           width: '100%',
           borderCollapse: 'collapse',
           fontSize: '14px',
         }}>
           <thead>
             <tr>
               <th style={{
                 padding: '16px 10px',
                 textAlign: 'center',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
                 width: '40px',
               }}>
                 <div style={{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   width: '20px',
                   height: '20px',
                   cursor: 'pointer',
                 }}
                 onClick={handleSelectAllAssignments}
                 >
                   {selectedAssignments.length === filteredAssignments.length ? (
                     <CheckCircle size={18} style={{ color: getColor('primary') }} />
                   ) : (
                     <div style={{
                       width: '18px',
                       height: '18px',
                       border: `2px solid ${getColor('border')}`,
                       borderRadius: '2px',
                     }} />
                   )}
                 </div>
               </th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Zimmer</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Gebäude</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Lernende</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>E-Mail</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Letztes Update</th>
               <th style={{
                 textAlign: 'left',
                 padding: '16px 20px',
                 fontSize: '14px',
                 fontWeight: '600',
                 color: getColor('textMedium'),
                 borderBottom: `1px solid ${getColor('border')}`,
                 backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#f8fafc',
                 position: 'sticky',
                 top: 0,
               }}>Aktionen</th>
             </tr>
           </thead>
           <tbody>
             {filteredAssignments.map((assignment) => (
               <tr 
                 key={assignment.userId} 
                 style={{
                   borderBottom: `1px solid ${getColor('border')}`,
                   backgroundColor: preferredUsers.includes(assignment.email) 
                     ? isDarkMode ? 'rgba(6, 182, 212, 0.08)' : '#ecfeff'
                     : 'transparent',
                   transition: 'background-color 0.2s ease',
                   '&:hover': {
                     backgroundColor: isDarkMode ? COLORS.dark.surfaceHover : '#f8fafc',
                   },
                 }}
               >
                 <td style={{
                   padding: '16px 10px',
                   textAlign: 'center',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     width: '20px',
                     height: '20px',
                     cursor: 'pointer',
                   }}
                   onClick={() => handleToggleAssignmentSelection(assignment.userId)}
                   >
                     {selectedAssignments.includes(assignment.userId) ? (
                       <CheckCircle size={18} style={{ color: getColor('primary') }} />
                     ) : (
                       <div style={{
                         width: '18px',
                         height: '18px',
                         border: `2px solid ${getColor('border')}`,
                         borderRadius: '2px',
                       }} />
                     )}
                   </div>
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'inline-flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     minWidth: '36px',
                     height: '36px',
                     padding: '0 12px',
                     borderRadius: '10px',
                     backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#cffafe',
                     color: getColor('accent'),
                     fontWeight: '600',
                     fontSize: '15px',
                   }}>
                     {assignment.roomNumber}
                   </div>
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>
                   {assignment.building ? (
                     <div style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       gap: '6px',
                       padding: '6px 12px',
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                       borderRadius: '8px',
                       fontSize: '13px',
                       color: getColor('textMedium'),
                     }}>
                       <Building size={14} />
                       <span>{assignment.building}</span>
                     </div>
                   ) : (
                     <span style={{
                       color: getColor('textLight'),
                       fontStyle: 'italic',
                     }}>-</span>
                   )}
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '10px',
                   }}>
                     <div style={{
                       width: '32px',
                       height: '32px',
                       borderRadius: '16px',
                       overflow: 'hidden',
                       backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                     }}>
                       {assignment.profileImagePath ? (
                         <img 
                           src={assignment.profileImagePath.startsWith('http') ? assignment.profileImagePath : `${BASE_URL}${assignment.profileImagePath}`} 
                           alt={assignment.username} 
                           style={{
                             width: '100%',
                             height: '100%',
                             objectFit: 'cover',
                           }} 
                           onError={(e) => { e.target.src = '/api/placeholder/64/64'; }}
                         />
                       ) : (
                         <div style={{
                           fontSize: '14px',
                           fontWeight: '600',
                           color: getColor('textLight'),
                         }}>
                           {assignment.username.split(' ').map(name => name[0]).join('')}
                         </div>
                       )}
                     </div>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center',
                       gap: '6px',
                     }}>
                       {preferredUsers.includes(assignment.email) && (
                         <Star size={14} style={{
                           color: getColor('warning'),
                         }} />
                       )}
                       <span>{assignment.username}</span>
                     </div>
                   </div>
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>{assignment.email}</td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('textMedium'),
                   verticalAlign: 'middle',
                 }}>
                   {assignment.assignmentDate 
                     ? new Date(assignment.assignmentDate).toLocaleString('de-DE')
                     : '-'}
                 </td>
                 <td style={{
                   padding: '16px 20px',
                   fontSize: '14px',
                   color: getColor('text'),
                   verticalAlign: 'middle',
                 }}>
                   <div style={{
                     display: 'flex',
                     gap: '8px',
                   }}>
                     <button 
                       onClick={() => openEditForm(assignment)}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         width: '34px',
                         height: '34px',
                         backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                         border: `1px solid ${getColor('border')}`,
                         borderRadius: '8px',
                         color: getColor('textMedium'),
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         '&:hover': {
                           backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                           color: getColor('text'),
                           transform: 'translateY(-2px)',
                         },
                       }}
                       title="Bearbeiten"
                     >
                       <Edit size={16} />
                     </button>
                     <button 
                       onClick={() => handleDeleteAssignment(assignment)}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         width: '34px',
                         height: '34px',backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                         border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'}`,
                         borderRadius: '8px',
                         color: getColor('error'),
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         '&:hover': {
                           backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fecaca',
                           transform: 'translateY(-2px)',
                         },
                       }}
                       title="Löschen"
                     >
                       <Trash size={16} />
                     </button>
                   </div>
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
     )}
   </div>
 );

 const renderAssignmentForm = () => {
   if (!showManualForm) return null;
   
   return (
     <div className="modal-overlay" style={{
       position: 'fixed',
       top: '0',
       left: '0',
       right: '0',
       bottom: '0',
       backgroundColor: 'rgba(0, 0, 0, 0.6)',
       backdropFilter: 'blur(8px)',
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       zIndex: '100',
       animation: 'fadeIn 0.3s ease-out',
     }}>
       <div 
         className="modal-content"
         style={{
           width: '100%',
           maxWidth: '560px',
           backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
           borderRadius: '20px',
           boxShadow: `0 20px 60px rgba(0, 0, 0, ${isDarkMode ? '0.4' : '0.15'})`,
           padding: '28px',
           maxHeight: '90vh',
           overflowY: 'auto',
           animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
         }}
       >
         <div style={{
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center',
           marginBottom: '24px',
           borderBottom: `1px solid ${getColor('border')}`,
           paddingBottom: '20px',
         }}>
           <h4 style={{
             fontSize: '20px',
             fontWeight: '600',
             color: getColor('text'),
             margin: '0',
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '32px',
               height: '32px',
               borderRadius: '16px',
               backgroundColor: formMode === 'add' 
                 ? isDarkMode ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5'
                 : isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#eef2ff',
               color: formMode === 'add' ? getColor('success') : getColor('primary'),
             }}>
               {formMode === 'add' ? <Plus size={18} /> : <Edit size={18} />}
             </div>
             {formMode === 'add' ? 'Neue Zimmerzuweisung' : 'Zimmerzuweisung bearbeiten'}
           </h4>
           <button 
             onClick={() => setShowManualForm(false)} 
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '36px',
               height: '36px',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
               border: 'none',
               borderRadius: '18px',
               color: getColor('textMedium'),
               cursor: 'pointer',
               transition: 'all 0.2s ease',
               '&:hover': {
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                 color: getColor('text'),
               },
             }}
           >
             <X size={20} />
           </button>
         </div>
         
         <form onSubmit={handleSubmitManualForm} style={{
           display: 'flex',
           flexDirection: 'column',
           gap: '24px',
         }}>
           <div style={{
             display: 'flex',
             flexDirection: 'column',
             gap: '10px',
           }}>
             <label style={{
               fontSize: '15px',
               fontWeight: '500',
               color: getColor('textMedium'),
             }}>Lernende</label>
             {formMode === 'add' ? (
               <>
                 <select
                   name="email"
                   value={manualFormData.email}
                   onChange={handleFormInputChange}
                   style={{
                     padding: '14px 16px',
                     borderRadius: '12px',
                     border: `1px solid ${formErrors.email 
                       ? getColor('error') 
                       : getColor('border')}`,
                     fontSize: '15px',
                     outline: 'none',
                     backgroundColor: isDarkMode 
                       ? formErrors.email 
                         ? 'rgba(239, 68, 68, 0.1)' 
                         : 'rgba(255, 255, 255, 0.05)'
                       : formErrors.email 
                         ? '#fee2e2' 
                         : '#f8fafc',
                     color: getColor('text'),
                     transition: 'all 0.2s ease',
                     appearance: 'none',
                     backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${
                       isDarkMode ? encodeURIComponent('#94a3b8') : encodeURIComponent('#64748b')
                     }' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                     backgroundRepeat: 'no-repeat',
                     backgroundPosition: 'right 16px center',
                     backgroundSize: '16px',
                     '&:focus': {
                       borderColor: getColor('primary'),
                       boxShadow: `0 0 0 3px ${isDarkMode 
                         ? 'rgba(99, 102, 241, 0.2)' 
                         : 'rgba(99, 102, 241, 0.1)'}`,
                     },
                   }}
                 >
                   <option value="">-- Lernende auswählen --</option>
                   {getSortedUnassignedUsers().map(user => (
                     <option key={user.id} value={user.email}>
                       {user.username} ({user.email})
                       {preferredUsers.includes(user.email) ? ' ⭐' : ''}
                     </option>
                   ))}
                 </select>
                 {formErrors.email && (
                   <div style={{
                     fontSize: '13px',
                     color: getColor('error'),
                     marginTop: '4px',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '6px',
                   }}>
                     <AlertCircle size={14} />
                     {formErrors.email}
                   </div>
                 )}
               </>
             ) : (
               <div style={{
                 padding: '14px 16px',
                 borderRadius: '12px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                 fontSize: '15px',
                 color: getColor('text'),
                 border: `1px solid ${getColor('border')}`,
                 display: 'flex',
                 alignItems: 'center',
                 gap: '10px',
               }}>
                 {findUserByEmail(manualFormData.email)?.profileImagePath ? (
                   <img 
                     src={findUserByEmail(manualFormData.email)?.profileImagePath.startsWith('http') 
                       ? findUserByEmail(manualFormData.email)?.profileImagePath 
                       : `${BASE_URL}${findUserByEmail(manualFormData.email)?.profileImagePath}`
                     } 
                     alt={findUserByEmail(manualFormData.email)?.username || 'Benutzer'} 
                     style={{
                       width: '28px',
                       height: '28px',
                       borderRadius: '14px',
                       objectFit: 'cover',
                     }} 
                     onError={(e) => { e.target.src = '/api/placeholder/56/56'; }}
                   />
                 ) : (
                   <div style={{
                     width: '28px',
                     height: '28px',
                     borderRadius: '14px',
                     backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     fontSize: '13px',
                     fontWeight: '600',
                     color: getColor('textLight'),
                   }}>
                     {(findUserByEmail(manualFormData.email)?.username || manualFormData.email)
                       .split(' ')
                       .map(name => name[0])
                       .join('')}
                   </div>
                 )}
                 <span>
                   {findUserByEmail(manualFormData.email)?.username || manualFormData.email}
                 </span>
               </div>
             )}
           </div>
           
           <div style={{
             display: 'flex',
             gap: '20px',
             flexWrap: 'wrap',
           }}>
             <div style={{
               display: 'flex',
               flexDirection: 'column',
               gap: '10px',
               flex: '1 1 220px',
             }}>
               <label style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Zimmernummer</label>
               <input
                 type="text"
                 name="roomNumber"
                 value={manualFormData.roomNumber}
                 onChange={handleFormInputChange}
                 placeholder="z.B. 101"
                 style={{
                   padding: '14px 16px',
                   borderRadius: '12px',
                   border: `1px solid ${formErrors.roomNumber 
                     ? getColor('error') 
                     : getColor('border')}`,
                   fontSize: '15px',
                   outline: 'none',
                   backgroundColor: isDarkMode 
                     ? formErrors.roomNumber 
                       ? 'rgba(239, 68, 68, 0.1)' 
                       : 'rgba(255, 255, 255, 0.05)'
                     : formErrors.roomNumber 
                       ? '#fee2e2' 
                       : '#f8fafc',
                   color: getColor('text'),
                   transition: 'all 0.2s ease',
                   '&:focus': {
                     borderColor: getColor('primary'),
                     boxShadow: `0 0 0 3px ${isDarkMode 
                       ? 'rgba(99, 102, 241, 0.2)' 
                       : 'rgba(99, 102, 241, 0.1)'}`,
                   },
                   '&::placeholder': {
                     color: getColor('textLight'),
                   },
                 }}
               />
               {formErrors.roomNumber && (
                 <div style={{
                   fontSize: '13px',
                   color: getColor('error'),
                   marginTop: '4px',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '6px',
                 }}>
                   <AlertCircle size={14} />
                   {formErrors.roomNumber}
                 </div>
               )}
             </div>
             
             <div style={{
               display: 'flex',
               flexDirection: 'column',
               gap: '10px',
               flex: '1 1 220px',
             }}>
               <label style={{
                 fontSize: '15px',
                 fontWeight: '500',
                 color: getColor('textMedium'),
               }}>Gebäude (optional)</label>
               <input
                 type="text"
                 name="building"
                 value={manualFormData.building}
                 onChange={handleFormInputChange}
                 placeholder="z.B. Haupthaus"
                 style={{
                   padding: '14px 16px',
                   borderRadius: '12px',
                   border: `1px solid ${getColor('border')}`,
                   fontSize: '15px',
                   outline: 'none',
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                   color: getColor('text'),
                   transition: 'all 0.2s ease',
                   '&:focus': {
                     borderColor: getColor('primary'),
                     boxShadow: `0 0 0 3px ${isDarkMode 
                       ? 'rgba(99, 102, 241, 0.2)' 
                       : 'rgba(99, 102, 241, 0.1)'}`,
                   },
                   '&::placeholder': {
                     color: getColor('textLight'),
                   },
                 }}
               />
             </div>
           </div>
           
           <div style={{
             display: 'flex',
             justifyContent: 'flex-end',
             gap: '16px',
             marginTop: '16px',
             borderTop: `1px solid ${getColor('border')}`,
             paddingTop: '24px',
           }}>
             <button 
               type="button" 
               onClick={() => setShowManualForm(false)}
               style={{
                 padding: '12px 20px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 color: getColor('textMedium'),
                 border: `1px solid ${getColor('border')}`,
                 borderRadius: '12px',
                 fontSize: '15px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   color: getColor('text'),
                   transform: 'translateY(-2px)',
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               Abbrechen
             </button>
             <button 
               type="submit" 
               style={{
                 padding: '12px 24px',
                 backgroundColor: getColor('primary'),
                 color: '#ffffff',
                 border: 'none',
                 borderRadius: '12px',
                 fontSize: '15px',
                 fontWeight: '500',
                 cursor: 'pointer',
                 boxShadow: `0 4px 12px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.2'})`,
                 transition: 'all 0.3s ease',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 '&:hover': {
                   backgroundColor: getColor('primaryDark'),
                   transform: 'translateY(-2px)',
                   boxShadow: `0 6px 16px rgba(99, 102, 241, ${isDarkMode ? '0.4' : '0.3'})`,
                 },
                 '&:active': {
                   transform: 'translateY(0)',
                 },
               }}
             >
               {formMode === 'add' ? (
                 <>
                   <Plus size={18} />
                   <span>Hinzufügen</span>
                 </>
               ) : (
                 <>
                   <CheckCircle size={18} />
                   <span>Speichern</span>
                 </>
               )}
             </button>
           </div>
         </form>
       </div>
     </div>
   );
 };

 // ========== ROOM IMAGE UPLOAD MODAL ==========
 const renderRoomImageUploadModal = () => {
   if (!showImageUploadModal || !selectedRoomForImage) return null;
   
   return (
     <div className="modal-overlay" style={{
       position: 'fixed',
       top: '0',
       left: '0',
       right: '0',
       bottom: '0',
       backgroundColor: 'rgba(0, 0, 0, 0.6)',
       backdropFilter: 'blur(8px)',
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       zIndex: '100',
       animation: 'fadeIn 0.3s ease-out',
     }}>
       <div 
         className="modal-content"
         style={{
           width: '100%',
           maxWidth: '600px',
           backgroundColor: isDarkMode ? COLORS.dark.surfaceAlt : '#ffffff',
           borderRadius: '20px',
           boxShadow: `0 20px 60px rgba(0, 0, 0, ${isDarkMode ? '0.4' : '0.15'})`,
           padding: '0',
           animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
         }}
       >
         {/* Header */}
         <div style={{
           padding: '24px',
           borderBottom: `1px solid ${getColor('border')}`,
           display: 'flex',
           justifyContent: 'space-between',
           alignItems: 'center',
         }}>
           <div style={{
             display: 'flex',
             alignItems: 'center',
             gap: '16px',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '48px',
               height: '48px',
               borderRadius: '14px',
               backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.15)' : '#ede9fe',
               color: getColor('primary'),
             }}>
               <Image size={24} />
             </div>
             <div>
               <h3 style={{
                 margin: '0 0 4px 0',
                 fontSize: '20px',
                 fontWeight: '700',
                 color: getColor('text'),
               }}>
                 Raumbild hochladen
               </h3>
               <div style={{
                 fontSize: '14px',
                 color: getColor('textMedium'),
               }}>
                 Zimmer {selectedRoomForImage.roomNumber}
               </div>
             </div>
           </div>
           <button 
             onClick={() => {
               setShowImageUploadModal(false);
               setImageUploadFile(null);
               setImageUploadPreview(null);
             }}
             style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '36px',
               height: '36px',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
               border: 'none',
               borderRadius: '18px',
               color: getColor('textMedium'),
               cursor: 'pointer',
             }}
           >
             <X size={20} />
           </button>
         </div>
         
         {/* Content */}
         <div style={{
           padding: '24px',
         }}>
           {/* Image Preview or Upload Area */}
           {imageUploadPreview ? (
             <div style={{
               marginBottom: '20px',
               textAlign: 'center',
             }}>
               <img 
                 src={imageUploadPreview} 
                 alt="Preview" 
                 style={{
                   maxWidth: '100%',
                   maxHeight: '300px',
                   borderRadius: '12px',
                   boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                 }}
               />
               <button
                 onClick={() => {
                   setImageUploadFile(null);
                   setImageUploadPreview(null);
                 }}
                 style={{
                   marginTop: '12px',
                   padding: '8px 16px',
                   backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                   border: 'none',
                   borderRadius: '8px',
                   color: getColor('error'),
                   fontSize: '14px',
                   fontWeight: '500',
                   cursor: 'pointer',
                   display: 'flex',
                   alignItems: 'center',
                   gap: '8px',
                   margin: '12px auto 0',
                 }}
               >
                 <X size={16} />
                 Entfernen
               </button>
             </div>
           ) : (
             <div style={{
               border: `2px dashed ${getColor('border')}`,
               borderRadius: '12px',
               padding: '40px',
               textAlign: 'center',
               marginBottom: '20px',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc',
             }}>
               <input
                 type="file"
                 accept="image/*"
                 onChange={handleImageFileSelect}
                 style={{ display: 'none' }}
                 id="room-image-upload"
               />
               <label 
                 htmlFor="room-image-upload"
                 style={{
                   cursor: 'pointer',
                   display: 'block',
                 }}
               >
                 <Image size={48} style={{
                   color: getColor('textLight'),
                   marginBottom: '12px',
                 }} />
                 <div style={{
                   fontSize: '16px',
                   fontWeight: '600',
                   color: getColor('text'),
                   marginBottom: '8px',
                 }}>
                   Bild auswählen
                 </div>
                 <div style={{
                   fontSize: '14px',
                   color: getColor('textLight'),
                 }}>
                   PNG, JPG bis 5MB
                 </div>
               </label>
             </div>
           )}
           
           {/* Action Buttons */}
           <div style={{
             display: 'flex',
             gap: '12px',
           }}>
             <button
               onClick={() => {
                 setShowImageUploadModal(false);
                 setImageUploadFile(null);
                 setImageUploadPreview(null);
               }}
               style={{
                 flex: 1,
                 padding: '12px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 border: 'none',
                 borderRadius: '10px',
                 color: getColor('text'),
                 fontSize: '15px',
                 fontWeight: '600',
                 cursor: 'pointer',
               }}
             >
               Abbrechen
             </button>
             <button
               onClick={handleUploadRoomImage}
               disabled={!imageUploadFile || uploadingRoomImage}
               style={{
                 flex: 1,
                 padding: '12px',
                 backgroundColor: imageUploadFile ? getColor('primary') : getColor('border'),
                 border: 'none',
                 borderRadius: '10px',
                 color: '#ffffff',
                 fontSize: '15px',
                 fontWeight: '600',
                 cursor: imageUploadFile ? 'pointer' : 'not-allowed',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 gap: '8px',
                 opacity: uploadingRoomImage ? 0.6 : 1,
               }}
             >
               {uploadingRoomImage ? (
                 <>
                   <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                   Hochladen...
                 </>
               ) : (
                 <>
                   <Upload size={16} />
                   Hochladen
                 </>
               )}
             </button>
           </div>
         </div>
       </div>
     </div>
   );
 };

 // Main render
 return (
   <div style={{
     backgroundColor: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
     borderRadius: '20px',
     padding: '24px',
     boxShadow: `0 4px 24px rgba(0, 0, 0, ${isDarkMode ? '0.2' : '0.05'})`,
     margin: '20px 0',
     transition: 'all 0.3s ease',
     maxWidth: '3200px',
     overflow: 'hidden',
     color: getColor('text'),
   }}>
     {/* Notification banner */}
     {renderNotificationBanner()}
     
     {/* Confirmation dialog */}
     {renderConfirmationDialog()}

     {/* Room detail modal */}
     {renderRoomDetailModal()}

     {/* Room image upload modal */}
     {renderRoomImageUploadModal()}

     {/* Header */}
     <div 
       ref={headerRef}
       style={{
         position: 'sticky',
         top: '0',
         zIndex: '20',
         backgroundColor: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
         padding: '16px',
         marginLeft: '-24px',
         marginRight: '-24px',
         marginTop: '-24px',
         marginBottom: '24px',
         transition: 'all 0.3s ease',
       }}
     >
       <div style={{
         display: 'flex',
         justifyContent: 'space-between',
         alignItems: 'center',
         marginBottom: '24px',
         flexWrap: 'wrap',
         gap: '16px',
       }}>
         <div style={{
           display: 'flex',
           alignItems: 'center',
           gap: '16px',
         }}>
           <h2 style={{
             fontSize: '28px',
             fontWeight: '700',
             color: getColor('text'),
             margin: 0,
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
           }}>
             <div style={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               padding: '8px',
               borderRadius: '12px',
               backgroundImage: isDarkMode 
                 ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(6, 182, 212, 0.3))'
                 : 'linear-gradient(135deg, #da291c,rgb(255, 255, 255))',
               boxShadow: isDarkMode
                 ? '0 8px 16px rgba(99, 102, 241, 0.2), 0 4px 6px rgba(6, 182, 212, 0.2)'
                 : '0 8px 16px rgba(99, 102, 241, 0.15), 0 4px 6px rgba(6, 182, 212, 0.15)',
               color: '#ffffff',
             }}>
               <Sparkles size={24} />
             </div>
              Zimmerverwaltung
           </h2>
           <div style={{
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
           }}>
             <button
               onClick={toggleDarkMode}
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '40px',
                 height: '40px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 border: 'none',
                 borderRadius: '20px',
                 cursor: 'pointer',
                 color: getColor('textMedium'),
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   transform: 'translateY(-2px)',
                 },
               }}
               title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
             >
               {isDarkMode ? '☀️' : '🌙'}
             </button>
             <button
               onClick={() => {
                 // Reset to default settings
                 setActiveTab('roomview');
                 setViewMode('cards');
                 setShowManualForm(false);
                 setShowExportPanel(false);
                 setShowAiInfo(false);
                 setShowAiSuggestions(false);
               }}
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '40px',
                 height: '40px',
                 backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                 border: 'none',
                 borderRadius: '20px',
                 cursor: 'pointer',
                 color: getColor('textMedium'),
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                   transform: 'translateY(-2px)',
                 },
               }}
               title="Einstellungen zurücksetzen"
             >
               <Settings size={20} />
             </button>
           </div>
         </div>
         <div style={{
           position: 'relative',
           width: '360px',
           maxWidth: '100%',
         }}>
           <input
             type="text"
             placeholder="Suche nach Lernenden oder Zimmer..."
             value={searchText}
             onChange={handleSearch}
             style={{
               width: '100%',
               padding: '14px 48px',
               borderRadius: '14px',
               border: `1px solid ${getColor('border')}`,
               fontSize: '15px',
               outline: 'none',
               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
               color: getColor('text'),
               transition: 'all 0.2s ease',
               '&:focus': {
                 borderColor: getColor('primary'),
                 boxShadow: `0 0 0 3px ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
               },
               '&::placeholder': {
                 color: getColor('textLight'),
               },
             }}
           />
           <Search size={20} style={{
             position: 'absolute',
             left: '16px',
             top: '50%',
             transform: 'translateY(-50%)',
             color: getColor('textLight'),
           }} />
           {searchText && (
             <button
               onClick={clearSearch}
               style={{
                 position: 'absolute',
                 right: '16px',
                 top: '50%',
                 transform: 'translateY(-50%)',
                 background: 'none',
                 border: 'none',
                 padding: '4px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 borderRadius: '50%',
                 color: getColor('textLight'),
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                   color: getColor('text'),
                 }
               }}
             >
               <X size={16} />
             </button>
           )}
         </div>
       </div>

       <div style={{
         display: 'flex',
         gap: '10px',
         borderBottom: `1px solid ${getColor('border')}`,
         paddingBottom: '14px',
         overflowX: 'auto',
         scrollbarWidth: 'none',
         msOverflowStyle: 'none',
         '&::-webkit-scrollbar': {
           display: 'none',
         },
       }}>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'roomview'
               ? isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'roomview'
               ? getColor('primary')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'roomview' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'roomview'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
             }
           }}
           onClick={() => setActiveTab('roomview')}
         >
           <Layers size={18} />
           <span>Zimmer-Übersicht</span>
         </button>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'upload'
               ? isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'upload'
               ? getColor('primary')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'upload' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'upload'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
             }
           }}
           onClick={() => setActiveTab('upload')}
         >
           <Upload size={18} />
           <span>Smart Import</span>
         </button>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'assigned'
               ? isDarkMode ? 'rgba(6, 182, 212, 0.15)' : '#ecfeff'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'assigned'
               ? getColor('accent')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'assigned' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'assigned'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#bae6fd',
             }
           }}
           onClick={() => setActiveTab('assigned')}
         >
           <Home size={18} />
           <span>Zimmerliste</span>
           <span style={{
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             minWidth: '24px',
             height: '24px',
             padding: '0 8px',
             borderRadius: '12px',
             backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#a5f3fc',
             color: isDarkMode ? COLORS.dark.accent : COLORS.light.accent,
             fontSize: '13px',
             fontWeight: '600',
             opacity: animatedCount ? 0 : 1,
             animation: animatedCount ? 'counterFadeIn 1s forwards' : 'none',
             animationDelay: '0.2s',
           }}>
             {stats.assigned}
           </span>
         </button>
         <button 
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '10px',
             padding: '12px 20px',
             backgroundColor: activeTab === 'unassigned'
               ? isDarkMode ? 'rgba(236, 72, 153, 0.15)' : '#fce7f3'
               : 'transparent',
             border: 'none',
             borderRadius: '12px',
             color: activeTab === 'unassigned'
               ? getColor('secondary')
               : getColor('textLight'),
             fontSize: '15px',
             fontWeight: activeTab === 'unassigned' ? '600' : '500',
             cursor: 'pointer',
             transition: 'all 0.2s ease',
             '&:hover': {
               backgroundColor: activeTab !== 'unassigned'
                 ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                 : isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
             }
           }}
           onClick={() => setActiveTab('unassigned')}
         >
           <User size={18} />
           <span>Ohne Zimmer</span>
           <span style={{
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             minWidth: '24px',
             height: '24px',
             padding: '0 8px',
             borderRadius: '12px',
             backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
             color: isDarkMode ? COLORS.dark.secondary : COLORS.light.secondary,
             fontSize: '13px',
             fontWeight: '600',
             opacity: animatedCount ? 0 : 1,
             animation: animatedCount ? 'counterFadeIn 1s forwards' : 'none',
             animationDelay: '0.4s',
           }}>
             {stats.unassigned}
           </span>
         </button>
       </div>
     </div>

     {/* Stats Container */}
     <div style={{
       display: 'flex',
       gap: '16px',
       marginBottom: '32px',
       flexWrap: 'wrap',
     }}>
       <div 
         ref={el => statsRef.current[0] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : '#ecfeff',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(6, 182, 212, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(6, 182, 212, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(6, 182, 212, 0.2)' : '#a5f3fc',
           color: getColor('accent'),
         }}>
           <Home size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('accent'),
             lineHeight: '1.2',
           }}>
             {stats.assigned}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Zugewiesene Zimmer
           </div>
         </div>
       </div>
       <div 
         ref={el => statsRef.current[1] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#eef2ff',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(99, 102, 241, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(99, 102, 241, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#c7d2fe',
           color: getColor('primary'),
         }}>
           <Users size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('primary'),
             lineHeight: '1.2',
           }}>
             {stats.total}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Gesamt Lernende
           </div>
         </div>
       </div>
       <div 
         ref={el => statsRef.current[2] = el}
         style={{
           flex: '1 1 calc(33.333% - 16px)',
           minWidth: '220px',
           backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.1)' : '#fce7f3',
           borderRadius: '16px',
           padding: '24px',
           display: 'flex',
           alignItems: 'center',
           gap: '20px',
           boxShadow: `0 4px 15px rgba(236, 72, 153, ${isDarkMode ? '0.15' : '0.07'})`,
           transition: 'transform 0.3s ease, box-shadow 0.3s ease',
           '&:hover': {
             transform: 'translateY(-4px)',
             boxShadow: `0 10px 20px rgba(236, 72, 153, ${isDarkMode ? '0.3' : '0.1'})`,
           },
         }}
       >
         <div style={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           width: '56px',
           height: '56px',
           borderRadius: '16px',
           backgroundColor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fbcfe8',
           color: getColor('secondary'),
         }}>
           <User size={28} />
         </div>
         <div>
           <div className={animatedCount ? 'animate-counter' : ''} style={{
             fontSize: '32px',
             fontWeight: '700',
             color: getColor('secondary'),
             lineHeight: '1.2',
           }}>
             {stats.unassigned}
           </div>
           <div style={{
             fontSize: '15px',
             color: isDarkMode ? COLORS.dark.textMedium : COLORS.light.textMedium,
           }}>
             Ohne Zimmer
           </div>
         </div>
       </div>
     </div>

     {/* Main Content Based on Active Tab */}
     <div>
       {activeTab === 'roomview' && renderRoomView()}
       {activeTab === 'upload' && renderUploadSection()}
       {activeTab === 'assigned' && renderAssignmentsSection()}
       {activeTab === 'unassigned' && renderUnassignedUsersSection()}
     </div>

     {/* Assignment Form Modal */}
     {renderAssignmentForm()}

     {/* CSS Animations */}
     <style>{`
       @keyframes fadeIn {
         from { opacity: 0; }
         to { opacity: 1; }
       }
       
       @keyframes fadeInUp {
         from {
           opacity: 0;
           transform: translateY(20px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes slideInUp {
         from {
           opacity: 0;
           transform: translateY(30px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes slideInDown {
         from {
           opacity: 0;
           transform: translateY(-20px);
         }
         to {
           opacity: 1;
           transform: translateY(0);
         }
       }
       
       @keyframes spin {
         from { transform: rotate(0deg); }
         to { transform: rotate(360deg); }
       }
       
       @keyframes pulse {
         0% { transform: scale(0.95); opacity: 0.7; }
          50% { transform: scale(1.05); opacity: 1; }
         100% { transform: scale(0.95); opacity: 0.7; }
       }
       
       @keyframes shimmer {
         from { transform: translateX(-100%); }
         to { transform: translateX(100%); }
       }
       
       @keyframes counterFadeIn {
         0% { opacity: 0; transform: scale(0.8); }
         70% { opacity: 1; transform: scale(1.1); }
         100% { opacity: 1; transform: scale(1); }
       }
       
       .notification-banner {
         animation: slideInDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
       }
       
       .modal-content {
         animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
       }
       
       .spinner {
         animation: spin 1s linear infinite;
       }
       
       .pulse-loader {
         animation: pulse 1.5s ease-in-out infinite;
       }
       
       .progress-shimmer {
         animation: shimmer 1.5s infinite;
       }
       
       .animate-counter {
         animation: counterFadeIn 1s forwards;
       }
       
       .typing-dots .dots {
         display: inline-block;
         animation: typingDots 1.4s infinite;
       }
       
       @keyframes typingDots {
         0%, 20% { content: '.'; }
         40% { content: '..'; }
         60%, 100% { content: '...'; }
       }
       
       .file-drop-zone label {
         transition: all 0.3s ease;
       }
       
       .file-drop-zone.drag-over label {
         transform: scale(0.98);
         background-color: ${isDarkMode ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff'};
         border-color: ${getColor('primary')};
         box-shadow: 0 0 0 3px ${isDarkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'};
       }
       
       /* Card hover effects */
       .user-card {
         transition: all 0.3s ease;
       }
       
       .user-card:hover {
         transform: translateY(-4px);
         box-shadow: 0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'});
       }
       
       .user-card:hover .user-assign-button {
         background-color: ${isDarkMode ? 'rgba(99, 102, 241, 0.25)' : '#c7d2fe'};
         transform: translateY(-2px);
       }
       
       .room-card {
         transition: all 0.3s ease;
       }
       
       .room-card:hover {
         transform: translateY(-4px);
         box-shadow: 0 10px 20px rgba(0, 0, 0, ${isDarkMode ? '0.25' : '0.1'});
       }
     `}</style>
     
   </div>
 );
};

export default PremiumRoomManagement;